import { generateClient } from "aws-amplify/api";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // ✅ important
import { useEffect, useRef, useState } from "react";

/* Legacy report-loading effects intentionally control their own fetch lifecycle. */
/* eslint-disable react-hooks/exhaustive-deps */
import { createPortal } from "react-dom";
import {
  createNotifications,
  createProfessionalReviews,
} from "../graphql/mutations.js";
import {
  listAssessmentAnswers,
  listAssessmentScores,
  listCreatePlans,
  listGeneralInformations,
  listProfessionalReviews,
  listUserLinks,
  listLabResults,
} from "../graphql/queries.js";
import { useConnectedClient } from "../ui-components/ConnectedClientContext.js";

const todayStr = new Date().toISOString().slice(0, 10);
const oneYearAgo = new Date();
oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
const oneYearAgoStr = oneYearAgo.toISOString().slice(0, 10);
// ✅ Patch the jsPDF instance (important!)

const client = generateClient();

const keyToQuestionMap = {
  // Trauma-related (boolean)
  TRHurtfulNames: "Were you called hurtful names",
  TRNoNurturing: "Was your environment lacking nurturing",
  TRHit: "Have you experienced physical hitting or abuse",
  TRNeedsNotMet: "Were your basic needs not met",
  TRForcedSex: "Have you experienced forced sexual acts",
  TRResponsible: "Were you wrongly held responsible for something",
  TRViolence: "Have you experienced violence",
  TRSubstanceAbuse: "Has substance abuse affected you or your family",
  TRMentalIllness: "Has mental illness affected you or your family",
  TRParentDivorce: "Have your parents divorced or separated",
  TRParentIncarcerated: "Has a parent been incarcerated",
  TRHomelessness: "Have you experienced homelessness",
  TRBodyFunction:
    "Have you experienced issues with body function or disability",
  TRNaturalDisaster: "Have you been affected by a natural disaster",
  TRDirectTerrorism: "Have you been directly affected by terrorism",
  TRIndirectTerrorism: "Have you been indirectly affected by terrorism",
  TRLovedOne: "Have you lost a loved one",
  TRBelief: "Have you experienced trauma related to your beliefs",
  TRResponseProfessional: "Did you receive help from a professional",
  TRResponseFamilyFriend: "Did you receive help from family or friends",
  TRResponseGod: "Did you find support through spiritual beliefs",
  TRResponseNoOne: "Did you receive no support",
  TRNoTrauma: "Have you experienced no trauma",

  // Coping Mechanisms (string frequencies)
  CMAlcohol: "Use alcohol to cope",
  CMDrugs: "Use drugs to cope",
  CMOvereating: "Overeat to cope",
  CMSmoking: "Smoke to cope",
  CMGaming: "Use gaming to cope",
  CMShopping: "Use shopping to cope",
  CMGambling: "Use gambling to cope",
  CMSex: "Use sex to cope",
  CMPorn: "Use pornography to cope",
  CMBlaming: "Blame others to cope",
  CMHurting: "Hurt yourself to cope",
  CMDisengage: "Disengage to cope",
  CMArt: "Use art to cope",
  CMMusic: "Use music to cope",
  CMPoetry: "Use poetry to cope",
  CMReading: "Use reading to cope",
  CMGroups: "Participate in groups to cope",
  CMCounseling: "Seek counseling to cope",
  CMVenting: "Vent to cope",
  CMWriting: "Write to cope",
  CMSensory: "Use sensory methods to cope",
  CMDancing: "Dance to cope",
  CMExercising: "Exercise to cope",
  CMWalking: "Walk to cope",
  CMChange: "Change environment to cope",
  CMAnalyze: "Analyze to cope",
  CMDaydream: "Daydream to cope",
  CMPositive: "Use positive thinking to cope",

  // Mental Health Diagnoses (boolean)
  MHDxPTSD: "Diagnosed with PTSD",
  MHDxDepression: "Diagnosed with Depression",
  MHDxSUD: "Diagnosed with Substance Use Disorder",
  MHDxOtherMental: "Diagnosed with other mental health conditions",

  // Mental Health Symptoms (string frequency)
  MHSleeplessness: "Experience sleeplessness",
  MHAlcoholUse: "Use alcohol",
  MHAnxiety: "Experience fright, flight, freeze",
  MHDepression: "Experience depression",
  MHDrugUse: "Use drugs",
  MHGrief: "Experience grief",
  MHGuilt: "Experience guilt",
  MHIrritability: "Experience irritability",
  MHStress: "Experience stress",
  MHRegret: "Experience regret",
  MHSuicidalThoughts: "Have suicidal thoughts",
  MHLoneliness: "Feel loneliness",
  MHWorry: "Feel worried / anxiety",
  MHHope: "Feel hopelessness",
  MHPeace: "Feel inner turmoil",
  MHJoy: "Feel emptiness",

  // Physical Health Diagnoses (boolean)
  PHDxInfertility: "Diagnosed with infertility",
  PHDxCurableSTD: "Diagnosed with a curable STD",
  PHDxIncurableSTD: "Diagnosed with an incurable STD",
  PHDxCancer: "Diagnosed with cancer",
  PHDxDiabetes: "Diagnosed with diabetes",
  PHDxHighBlood: "Diagnosed with high blood pressure",
  PHDxHeartDisease: "Diagnosed with heart disease",
  PHDxIrritableBowel: "Diagnosed with irritable bowel syndrome",
  PHDxVitA: "Vitamin A deficiency",
  PHDxVitB: "Vitamin B deficiency",
  PHDxVitC: "Vitamin C deficiency",
  PHDxVitD: "Vitamin D deficiency",
  PHDxVitE: "Vitamin E deficiency",
  PHDxVitK: "Vitamin K deficiency",
  PHDxAutoimmune: "Diagnosed with autoimmune condition",
  PHDxOtherPhysical: "Diagnosed with other physical health conditions",

  // Physical Health Behaviors (string frequency)
  PHVegeFruits: "Eat vegetables and fruits",
  PHBeanLentils: "Eat beans and lentils",
  PHGrainBreads: "Eat grains and breads",
  PHDairy: "Consume dairy products",
  PHMeat: "Eat meat",
  PHFishSeafood: "Eat fish and seafood",
  PHSweets: "Consume sweets",
  PHWater: "Drink water",
  PHPhysicalActivity: "Engage in physical activity",

  // Spiritual Health (strings)
  SHSpiritualDefine: "Spirituality defines lifestyle",
  SHSpiritualIntegrate: "Spirituality integrated into lifestyle",
  SHPrayer: "Frequency of prayer",
  SHSpiritualActivity: "Frequency of healthy spiritual activities",
  SHReadText: "Frequency of reading religious text",
  SHAlignText: "Frequency of aligning life with religious beliefs",
  SHCommunity: "Frequency of engaging with religious community",

  // Social Health (strings)
  SoHLearning: "Openness to learn and grow",
  SoHStable: "Financial stability",
  SoHSafety: "Safety and security",
  SoHKindness: "Kindness toward self and others",
  SoHForgiveness: "Forgiving of self and others",
  SoHPatience: "Patient with self and others",
  SoHRelationships: "Healthy relationships",
  SoHContribution: "Contributing to others / your community",
  SoHBoundaries: "Healthy boundaries",
  SoHEUnpleasant: "Experience unpleasant emotions",
  SoHEPleasant: "Experience pleasant emotions",
  SoHEControl: "Control over emotions",

  // Social Determinants of Health (strings)
  SDoHAgeRange: "Age range",
  SDoHRace: "Race",
  SDoHGeographicRegion: "Geographic region",
  SDoHEthnicity: "Ethnicity",
  SDoHState: "State",
  SDoHZipCode: "Zip code",
  SDoHMaritalStatus: "Marital status",
  SDoHMilitaryStatus: "Military status",
  SDoHGender: "Gender",
  SDoHEducation: "Highest education level",
  SDoHJobStatus: "Job status",
  SDoHIncome: "Income level",
  SDoHHousingStatus: "Housing status",
  SDoHHomeAsChild: "Home environment as a child",
  SDoHReligion: "Religion",
  SDoHDenomination: "Religious denomination",

  // System fields
  CompletedDate: "Assessment completed on",
  CompletedTime: "Assessment completed at",
  isActive: "Is this assessment currently active",
};

// Domains grouped by keys for rendering
const traumaKeys = [
  "TRHurtfulNames",
  "TRNoNurturing",
  "TRHit",
  "TRNeedsNotMet",
  "TRForcedSex",
  "TRResponsible",
  "TRViolence",
  "TRSubstanceAbuse",
  "TRMentalIllness",
  "TRParentDivorce",
  "TRParentIncarcerated",
  "TRHomelessness",
  "TRBodyFunction",
  "TRNaturalDisaster",
  "TRDirectTerrorism",
  "TRIndirectTerrorism",
  "TRLovedOne",
  "TRBelief",
];

const traumaResponseKeys = [
  "TRResponseProfessional",
  "TRResponseFamilyFriend",
  "TRResponseGod",
  "TRResponseNoOne",
  "TRNoTrauma",
];

const copingKeys = [
  "CMAlcohol",
  "CMDrugs",
  "CMOvereating",
  "CMSmoking",
  "CMGaming",
  "CMShopping",
  "CMGambling",
  "CMSex",
  "CMPorn",
  "CMBlaming",
  "CMHurting",
  "CMDisengage",
  "CMArt",
  "CMMusic",
  "CMPoetry",
  "CMReading",
  "CMGroups",
  "CMCounseling",
  "CMVenting",
  "CMWriting",
  "CMSensory",
  "CMDancing",
  "CMExercising",
  "CMWalking",
  "CMChange",
  "CMAnalyze",
  "CMDaydream",
  "CMPositive",
];

const mentalHealthDxKeys = [
  "MHDxPTSD",
  "MHDxDepression",
  "MHDxSUD",
  "MHDxOtherMental",
];

const mentalHealthSymptomKeys = [
  "MHSleeplessness",
  "MHAlcoholUse",
  "MHAnxiety",
  "MHDepression",
  "MHDrugUse",
  "MHGrief",
  "MHGuilt",
  "MHIrritability",
  "MHStress",
  "MHRegret",
  "MHSuicidalThoughts",
  "MHLoneliness",
  "MHWorry",
];

const physicalHealthDxKeys = [
  "PHDxInfertility",
  "PHDxCurableSTD",
  "PHDxIncurableSTD",
  "PHDxCancer",
  "PHDxDiabetes",
  "PHDxHighBlood",
  "PHDxHeartDisease",
  "PHDxIrritableBowel",
  "PHDxVitA",
  "PHDxVitB",
  "PHDxVitC",
  "PHDxVitD",
  "PHDxVitE",
  "PHDxVitK",
  "PHDxAutoimmune",
  "PHDxOtherPhysical",
];

const physicalHealthBehaviorKeys = [
  "PHVegeFruits",
  "PHBeanLentils",
  "PHGrainBreads",
  "PHDairy",
  "PHMeat",
  "PHFishSeafood",
  "PHSweets",
  "PHWater",
  "PHPhysicalActivity",
];

const spiritualHealthKeys = [
  "SHSpiritualDefine",
  "SHSpiritualIntegrate",
  "SHPrayer",
  "SHSpiritualActivity",
  "SHReadText",
  "SHAlignText",
  "SHCommunity",
];

const socialHealthKeys = [
  "SoHLearning",
  "SoHStable",
  "SoHSafety",
  "SoHKindness",
  "SoHForgiveness",
  "SoHPatience",
  "SoHRelationships",
  "SoHContribution",
  "SoHBoundaries",
  "SoHEUnpleasant",
  "SoHEPleasant",
  "SoHEControl",
];

const sdoHKeys = [
  "SDoHAgeRange",
  "SDoHRace",
  "SDoHGeographicRegion",
  "SDoHEthnicity",
  "SDoHState",
  "SDoHZipCode",
  "SDoHMaritalStatus",
  "SDoHMilitaryStatus",
  "SDoHGender",
  "SDoHEducation",
  "SDoHJobStatus",
  "SDoHIncome",
  "SDoHHousingStatus",
  "SDoHHomeAsChild",
  "SDoHReligion",
  "SDoHDenomination",
];
const LIST_TRACK_PLANS_CUSTOM = `
  query ListTrackPlans($filter: ModelTrackPlanFilterInput) {
    listTrackPlans(filter: $filter) {
      items {
        id
        userId
        createdAt
        Discontinued
        Successful
        Action
        ActionQuestion
        CompletedDate
        Frequency
        ActionNote
        DiscontinuedDate
        SuccessfulDate
        Percentage
        Update1Note
        Update1Date
        Update2Note
        Update2Date
        Update3Note
        Update3Date
        Update4Note
        Update4Date
        Update5Note
        Update5Date
        Update6Note
        Update6Date
        Update7Note
        Update7Date
        Update8Note
        Update8Date
      }
    }
  }
`;

// Combined keys to exclude from generic Q&A (to avoid duplicates)
const keysInLists = [
  ...traumaKeys,
  ...traumaResponseKeys,
  ...copingKeys,
  ...mentalHealthDxKeys,
  ...mentalHealthSymptomKeys,
  ...physicalHealthDxKeys,
  ...physicalHealthBehaviorKeys,
  ...spiritualHealthKeys,
  ...socialHealthKeys,
  ...sdoHKeys,
];

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 20,
    minHeight: "100vh",
    marginTop: -100,
  },
  title: {
    fontSize: 32,
    marginBottom: 24,
    textAlign: "center",
    color: "#003366",
  },
  planCard: {
    width: "100%",
    border: "2px solid #d8d8d8",
    borderRadius: 14,
    padding: 24,
    marginBottom: 32,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  planTitle: { marginBottom: -6, color: "#0067B3" },
  planQuestion: {
    marginBottom: 6,
    fontSize: 25,
    fontWeight: 600,
    color: "#0067B3",
  },
  planText: { marginBottom: 16, fontSize: 15, color: "#0067B3" },
  weeksGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 14,
    marginTop: 20,
  },
  weekButton: {
    borderColor: "#ccc",
    height: 60,
    width: "100%",
    border: "2px solid #ccc",
    padding: 10,
    borderRadius: 12,
    background: "#fff",
    cursor: "pointer",
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  weekButtonActive: {
    borderColor: "#4da6ff",
    background: "#d9eaff",
    boxShadow: "0 0 10px #4da6ffaa",
  },
  weekEmoji: {
    fontSize: 36,
    lineHeight: 1,
  },
  expandedWeek: {
    marginTop: 24,
    padding: 20,
    border: "2px solid #0077cc",
    borderRadius: 14,
    backgroundColor: "#fff",
    color: "#003366",
  },
  expandedWeekTitle: {
    marginBottom: 10,
    color: "#0077cc",
  },
  moodButtons: {
    display: "flex",
    gap: 24,
    marginBottom: 16,
    justifyContent: "center",
  },
  moodButton: {
    fontSize: 48,
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    userSelect: "none",
    padding: 0,
    transition: "transform 0.3s ease",
  },
  moodButtonSelected: {
    animation: "bounce 0.6s",
  },
  textarea: {
    width: "100%",
    padding: 12,
    fontSize: 15,
    borderRadius: 10,
    border: "1px solid #aaa",
    resize: "vertical",
    fontFamily: "inherit",
  },
  discontinueButton: {
    width: 130,
    marginTop: 12,
    padding: "8px 16px",
    backgroundColor: "transparent",
    border: "2px solid #cc4444",
    borderRadius: 12,
    color: "#cc4444",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.3s, color 0.3s",
    alignSelf: "flex-start",
  },
  editButton: {
    width: 130,
    marginTop: 12,
    padding: "8px 16px",
    backgroundColor: "#0077cc",
    border: "none",
    borderRadius: 12,
    color: "#fff",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.3s",
    alignSelf: "flex-start",
  },
  frequencySelect: {
    marginTop: 10,
    width: "100%",
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #0077cc",
    fontSize: 14,
    color: "#003366",
  },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "#ffffff",
    border: "2px solid #0067B3",
    borderRadius: "16px",
    padding: "30px 24px",
    width: "90%",
    maxWidth: "420px",
    textAlign: "center",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
    animation: "fadeInScale 0.3s ease-in-out",
  },
  modalButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginTop: "20px",
    flexWrap: "wrap",
  },
  modalButtonConfirm: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "999px",
    padding: "10px 20px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  modalButtonCancel: {
    backgroundColor: "#ccc",
    color: "#000",
    border: "none",
    borderRadius: "999px",
    padding: "10px 20px",
    fontWeight: "bold",
    cursor: "pointer",
  },
};

const RADIUS = 54;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function PercentageWheel({ percentage }) {
  const progress = Math.min(Math.max(percentage, 0), 100);
  const dashOffset = CIRCUMFERENCE - (progress / 100) * CIRCUMFERENCE;

  // Determine color based on percentage
  let strokeColor = "#cc4444"; // red default
  if (progress >= 80) {
    strokeColor = "#44cc44"; // green
  } else if (progress >= 70) {
    strokeColor = "#cccc44"; // yellow
  }

  return (
    <svg
      width={120}
      height={120}
      viewBox="0 0 120 120"
      aria-label={`Progress: ${progress}%`}
      role="img"
    >
      {/* Background circle */}
      <circle
        cx="60"
        cy="60"
        r={RADIUS}
        stroke="#e6e6e6"
        strokeWidth="12"
        fill="none"
      />
      {/* Progress arc */}
      <circle
        cx="60"
        cy="60"
        r={RADIUS}
        stroke={strokeColor}
        strokeWidth="12"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={dashOffset}
        style={{
          transition: "stroke-dashoffset 0.6s ease",
          transform: "rotate(-90deg)",
          transformOrigin: "50% 50%",
        }}
      />
      {/* Percentage Text */}
      <text
        x="50%"
        y="50%"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize="30"
        fontWeight="600"
        fill="#0067B3"
        fontFamily="Arial, sans-serif"
      >
        {progress}%
      </text>
    </svg>
  );
}

const categoryThresholds = {
  CMScore: [
    { min: 0, max: 1, color: "#28a745" },
    { min: 2, max: 3, color: "#ffc107" },
    { min: 4, max: 48, color: "#B00000" },
  ],
  TRScore: [
    { min: 0, max: 10, color: "#28a745" }, // green
    { min: 11, max: 20, color: "#ffc107" }, // yellow
    { min: 21, max: 40, color: "#B00000" }, // red
  ],

  MHScore: [
    { min: 0, max: 16, color: "#28a745" },
    { min: 17, max: 32, color: "#ffc107" },
    { min: 33, max: 66, color: "#B00000" },
  ],
  PHScore: [
    { min: 0, max: 5, color: "#28a745" },
    { min: 6, max: 10, color: "#ffc107" },
    { min: 11, max: 30, color: "#B00000" },
  ],
  SHScore: [
    { min: 0, max: 7, color: "#28a745" },
    { min: 8, max: 14, color: "#ffc107" },
    { min: 15, max: 28, color: "#B00000" },
  ],
  SoHScore: [
    { min: 0, max: 12, color: "#28a745" },
    { min: 13, max: 24, color: "#ffc107" },
    { min: 25, max: 48, color: "#B00000" },
  ],
};

function describeTextArc(cx, cy, radius, startAngle, endAngle) {
  const rad = (deg) => (deg * Math.PI) / 180;
  const startX = cx + radius * Math.cos(rad(startAngle));
  const startY = cy + radius * Math.sin(rad(startAngle));
  const endX = cx + radius * Math.cos(rad(endAngle));
  const endY = cy + radius * Math.sin(rad(endAngle));

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
}
function AssessmentDetails({ assessmentScores, categoryThresholds, answers }) {
  // Define your domains with keys & questions keys to include (excluding trauma and diagnoses)
  const domains = [
    {
      key: "CMScore",
      label: "Coping Mechanisms",
      questionKeys: [
        "CMAlcohol",
        "CMDrugs",
        "CMOvereating",
        "CMSmoking",
        "CMGaming",
        "CMShopping",
        "CMGambling",
        "CMSex",
        "CMPorn",
        "CMBlaming",
        "CMHurting",
        "CMDisengage",
        "CMArt",
        "CMMusic",
        "CMPoetry",
        "CMReading",
        "CMGroups",
        "CMCounseling",
        "CMVenting",
        "CMWriting",
        "CMSensory",
        "CMDancing",
        "CMExercising",
        "CMWalking",
        "CMChange",
        "CMAnalyze",
        "CMDaydream",
        "CMPositive",
      ],
    },
    {
      key: "MHScore",
      label: "Mental Health Symptoms",
      questionKeys: [
        "MHSleeplessness",
        "MHAlcoholUse",
        "MHAnxiety",
        "MHDepression",
        "MHDrugUse",
        "MHGrief",
        "MHGuilt",
        "MHIrritability",
        "MHStress",
        "MHRegret",
        "MHSuicidalThoughts",
        "MHLoneliness",
        "MHWorry",
        "MHHope",
        "MHPeace",
        "MHJoy",
      ],
    },
    {
      key: "PHScore",
      label: "Physical Health Behaviors",
      questionKeys: [
        "PHVegeFruits",
        "PHBeanLentils",
        "PHGrainBreads",
        "PHDairy",
        "PHMeat",
        "PHFishSeafood",
        "PHSweets",
        "PHWater",
        "PHPhysicalActivity",
      ],
    },
    {
      key: "SHScore",
      label: "Spiritual Health",
      questionKeys: [
        "SHSpiritualDefine",
        "SHSpiritualIntegrate",
        "SHPrayer",
        "SHSpiritualActivity",
        "SHReadText",
        "SHAlignText",
        "SHCommunity",
      ],
    },
    {
      key: "SoHScore",
      label: "Social Health",
      questionKeys: [
        "SoHLearning",
        "SoHStable",
        "SoHSafety",
        "SoHKindness",
        "SoHForgiveness",
        "SoHPatience",
        "SoHRelationships",
        "SoHContribution",
        "SoHBoundaries",
        "SoHEUnpleasant",
        "SoHEPleasant",
        "SoHEControl",
      ],
    },
  ];

  // Helper: get color for domain by score (same as your thresholds)
  const getDomainColor = (score, domainKey) => {
    const t = categoryThresholds[domainKey];
    if (!t) return "gray";
    for (const threshold of t) {
      if (score >= threshold.min && score <= threshold.max)
        return threshold.color;
    }
    return "gray";
  };

  const getQuestionColor = (value) => {
    if (value === undefined || value === null) return "gray";
    if (typeof value === "string") return "black"; // categorical strings no color fill
    if (value > 0 && value < 1) return "#9CA3AF";
    if (value >= 0 && value <= 1) return "#28a745"; // green
    if (value === 2) return "#ffc107"; // yellow
    if (value >= 3 && value <= 4) return "#B00000"; // red
    return "gray";
  };

  return (
    <div
      style={{
        maxWidth: 800,
        margin: "0 auto",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {domains.map(({ key, label, questionKeys, isCategorical }) => {
        const domainScore = assessmentScores?.[key];
        const domainColor = getDomainColor(domainScore, key);

        return (
          <section key={key} style={{ marginBottom: 40 }}>
            <h3
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                color: "#0067B3",
                borderBottom: "3px solid #0067B3",
                paddingBottom: 6,
                fontWeight: "700",
                fontSize: "1.3rem",
              }}
            >
              <span>{label}</span>
              <span
                style={{
                  minWidth: 20,
                  minHeight: 20,
                  borderRadius: "50%",
                  backgroundColor: domainColor,

                  display: "inline-block",
                }}
                title={`Domain score: ${domainScore}`}
              />
            </h3>

            <ul
              style={{ marginLeft: 24, listStyleType: "none", paddingLeft: 0 }}
            >
              {questionKeys.map((qKey) => {
                // Get the answer text/value from answers (not scores)
                const answerVal = answers?.[qKey];
                // Get the numeric score for coloring
                const scoreVal = assessmentScores?.[qKey];
                const color = isCategorical
                  ? "black"
                  : getQuestionColor(scoreVal);

                return (
                  <li
                    key={qKey}
                    style={{
                      display: "flex",
                      alignItems: "flex-start", // align circle to top of text
                      gap: 12,
                      marginBottom: 6,
                      color: "#333",
                    }}
                  >
                    {!isCategorical && (
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          flexShrink: 0,
                          borderRadius: "50%",
                          backgroundColor: color,

                          display: "inline-block",
                          marginTop: 2, // small adjustment so it visually aligns with first text line
                        }}
                        title={`Score: ${scoreVal}`}
                      />
                    )}
                    <span
                      style={{ flex: 1, textAlign: "left", lineHeight: 1.4 }}
                    >
                      <strong>{keyToQuestionMap?.[qKey]}</strong>:{" "}
                      {answerVal !== undefined ? answerVal.toString() : "N/A"}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// Helper function stays the same
function describeArc(cx, cy, innerRadius, outerRadius, startAngle, endAngle) {
  const startRad = (startAngle - 90) * (Math.PI / 180);
  const endRad = (endAngle - 90) * (Math.PI / 180);

  const x1Outer = cx + outerRadius * Math.cos(startRad);
  const y1Outer = cy + outerRadius * Math.sin(startRad);

  const x2Outer = cx + outerRadius * Math.cos(endRad);
  const y2Outer = cy + outerRadius * Math.sin(endRad);

  const x1Inner = cx + innerRadius * Math.cos(endRad);
  const y1Inner = cy + innerRadius * Math.sin(endRad);

  const x2Inner = cx + innerRadius * Math.cos(startRad);
  const y2Inner = cy + innerRadius * Math.sin(startRad);

  return `
    M ${x1Outer} ${y1Outer}
    A ${outerRadius} ${outerRadius} 0 0 1 ${x2Outer} ${y2Outer}
    L ${x1Inner} ${y1Inner}
    A ${innerRadius} ${innerRadius} 0 0 0 ${x2Inner} ${y2Inner}
    Z
  `;
}

// =======================
// 🔬 Lab fields + helpers
// =======================

const labsFields = [
  {
    key: "LabsTG",
    label: "Triglycerides",
    rangeText: "< 200 mg/dL",
    type: "lt",
    max: 200,
  },
  {
    key: "LabsTC",
    label: "Cholesterol (Total)",
    rangeText: "< 239 mg/dL",
    type: "lt",
    max: 239,
  },
  {
    key: "LabsHDL",
    label: "Cholesterol (High-Density Lipoprotein)",
    rangeText: "> 40 mg/dL",
    type: "gt",
    min: 40,
  },
  {
    key: "LabsLDL",
    label: "Cholesterol (Low-Density Lipoprotein)",
    rangeText: "< 100 mg/dL",
    type: "lt",
    max: 100,
  },
  {
    key: "LabsNa",
    label: "Sodium",
    rangeText: "136 – 145 mmol/L",
    type: "between",
    min: 136,
    max: 145,
  },
  {
    key: "LabsK",
    label: "Potassium",
    rangeText: "3.6 – 5.1 mmol/L",
    type: "between",
    min: 3.6,
    max: 5.1,
  },
  {
    key: "LabsP",
    label: "Phosphorus",
    rangeText: "2.5 – 4.5 mg/dL",
    type: "between",
    min: 2.5,
    max: 4.5,
  },
  {
    key: "LabsCl",
    label: "Chloride",
    rangeText: "98 – 107 mmol/L",
    type: "between",
    min: 98,
    max: 107,
  },
  {
    key: "LabsZn",
    label: "Zinc",
    rangeText: "80 – 120 mcg/dL",
    type: "between",
    min: 80,
    max: 120,
  },
  {
    key: "LabsCa",
    label: "Calcium",
    rangeText: "8.9 – 10.3 mg/dL",
    type: "between",
    min: 8.9,
    max: 10.3,
  },
  {
    key: "LabsMg",
    label: "Magnesium",
    rangeText: "1.8 – 2.5 mg/dL",
    type: "between",
    min: 1.8,
    max: 2.5,
  },
  {
    key: "LabsFe",
    label: "Iron",
    rangeText: "149 – 505 mcg/dL",
    type: "between",
    min: 149,
    max: 505,
  },
  {
    key: "LabsFerritin",
    label: "Ferritin",
    rangeText: "11 – 336 ng/mL",
    type: "between",
    min: 11,
    max: 336,
  },
  {
    key: "LabsVitA",
    label: "Vitamin A",
    rangeText: "20 – 60 mcg/dL",
    type: "between",
    min: 20,
    max: 60,
  },
  {
    key: "LabsVitB1",
    label: "Thiamin (B1)",
    rangeText: "2.5 – 7.5 μg/dL",
    type: "between",
    min: 2.5,
    max: 7.5,
  },
  {
    key: "LabsVitB2",
    label: "Riboflavin (B2)",
    rangeText: "106 – 638 nmol/L",
    type: "between",
    min: 106,
    max: 638,
  },
  {
    key: "LabsVitB3",
    label: "Niacin (B3)",
    rangeText: "0.50 – 8.45 μg/mL",
    type: "between",
    min: 0.5,
    max: 8.45,
  },
  {
    key: "LabsVitB5",
    label: "Pantothenic Acid (B5)",
    rangeText: "1.6 – 2.7 μmol/L",
    type: "between",
    min: 1.6,
    max: 2.7,
  },
  {
    key: "LabsVitB6",
    label: "Pyridoxine (B6)",
    rangeText: "5 – 50 μg/L",
    type: "between",
    min: 5,
    max: 50,
  },
  {
    key: "LabsVitB7",
    label: "Biotin (B7)",
    rangeText: "133 – 329 pmol/L",
    type: "between",
    min: 133,
    max: 329,
  },
  {
    key: "LabsVitB9",
    label: "Folate (B9)",
    rangeText: "> 4 ng/mL",
    type: "gt_or_eq",
    min: 4,
  },
  {
    key: "LabsVitB12",
    label: "Cobalamin (B12)",
    rangeText: "180 – 914 pg/mL",
    type: "between",
    min: 180,
    max: 914,
  },
  {
    key: "LabsVitC",
    label: "Vitamin C",
    rangeText: "2 – 19 mg/L",
    type: "between",
    min: 2,
    max: 19,
  },
  {
    key: "LabsVitD",
    label: "Vitamin D, 25-Hydroxy",
    rangeText: "30.0 – 100.0 ng/mL",
    type: "between",
    min: 30,
    max: 100,
  },
  {
    key: "LabsVitE",
    label: "Vitamin E (Alpha Tocopherol)",
    rangeText: "500 – 1800 μg/dL",
    type: "between",
    min: 500,
    max: 1800,
  },
  {
    key: "LabsVitK",
    label: "Vitamin K",
    rangeText: "0.22 – 4.88 nmol/L",
    type: "between",
    min: 0.22,
    max: 4.88,
  },
];

// Group keys into the 3 lab categories
const lipidLabKeys = ["LabsTG", "LabsTC", "LabsHDL", "LabsLDL"];

const mineralLabKeys = [
  "LabsNa",
  "LabsK",
  "LabsP",
  "LabsCl",
  "LabsZn",
  "LabsCa",
  "LabsMg",
  "LabsFe",
  "LabsFerritin",
];

const vitaminLabKeys = [
  "LabsVitA",
  "LabsVitB1",
  "LabsVitB2",
  "LabsVitB3",
  "LabsVitB5",
  "LabsVitB6",
  "LabsVitB7",
  "LabsVitB9",
  "LabsVitB12",
  "LabsVitC",
  "LabsVitD",
  "LabsVitE",
  "LabsVitK",
];

// Group each test into a category for UI sections
function getLabCategory(fieldKey) {
  if (lipidLabKeys.includes(fieldKey)) return "Lipids";
  if (mineralLabKeys.includes(fieldKey)) return "Minerals";
  if (vitaminLabKeys.includes(fieldKey)) return "Vitamins";
  return "Other";
}

// Simple color coding: green=normal, yellow=borderline, red=out of range
function getLabDotColor(fieldKey, rawValue) {
  const value = parseFloat(rawValue);
  if (Number.isNaN(value)) return "#999999"; // gray if not numeric

  // You can tune these per-test if you want.
  switch (fieldKey) {
    case "LabsTC":
      if (value < 200) return "#28a745"; // green
      if (value <= 239) return "#ffc107"; // yellow
      return "#B00000"; // red

    case "LabsLDL":
      if (value < 100) return "#28a745";
      if (value <= 159) return "#ffc107";
      return "#B00000";

    case "LabsHDL":
      if (value >= 60) return "#28a745";
      if (value >= 40) return "#ffc107";
      return "#B00000";

    case "LabsTG":
      if (value < 150) return "#28a745";
      if (value <= 199) return "#ffc107";
      return "#B00000";

    case "LabsVitD":
      if (value >= 30 && value <= 100) return "#28a745";
      if (value >= 20 && value < 30) return "#ffc107";
      return "#B00000";

    default:
      // Generic: assume "middle is good"
      return "#28a745";
  }
}

export default function ViewReports() {
  const containerRef = useRef(null);
  const [popupCenterX, setPopupCenterX] = useState(() => {
    if (typeof window === "undefined") return 0;
    return Math.round(window.innerWidth / 2);
  });
  const [assessments, setAssessments] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [viewFilter, setViewFilter] = useState("assessments"); // NEW: assessments or trackPlans
  const [trackPlans, setTrackPlans] = useState([]); // if you're loading track plans later
  const [showPercentages, setShowPercentages] = useState(true);
  const [assessmentScores, setAssessmentScores] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [startDate, setStartDate] = useState(oneYearAgoStr);
  // 🔹 Labs (single view)
  const [labResults, setLabResults] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);

  // 🔹 Compare mode (assessments vs labs)
  const [compareMode, setCompareMode] = useState("assessments"); // "assessments" | "labs"

  // ---- Assessment compare state ----
  const [compareLeftAssessmentId, setCompareLeftAssessmentId] = useState(null);
  const [compareRightAssessmentId, setCompareRightAssessmentId] =
    useState(null);
  const [compareLeftScores, setCompareLeftScores] = useState(null);
  const [compareRightScores, setCompareRightScores] = useState(null);

  // ---- Lab compare state ----
  const [compareLeftLabId, setCompareLeftLabId] = useState(null);
  const [compareRightLabId, setCompareRightLabId] = useState(null);

  const [endDate, setEndDate] = useState(todayStr);
  const { connectedClient } = useConnectedClient();
  const [linkIntervals, setLinkIntervals] = useState(null); // null = loading, [] = no link
  const [healthPlanReviews, setHealthPlanReviews] = useState([]);
  const [selectedFields, setSelectedFields] = useState({
    triggers: [],
    warningSigns: [],
    strengths: [],
    barriers: [],
  });

  const normalizeDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    return new Date(`${dateStr}T${timeStr || "00:00:00"}`);
  };

  const findBestScoreForAssessment = (assessment, scores) => {
    if (!assessment || !scores?.length) return null;

    const assessmentDateTime = normalizeDateTime(
      assessment.CompletedDate,
      assessment.CompletedTime,
    );

    if (assessmentDateTime) {
      const exact = scores.find((score) => {
        const scoreDateTime = normalizeDateTime(
          score.CompletedDate,
          score.CompletedTime,
        );
        return (
          scoreDateTime &&
          scoreDateTime.getTime() === assessmentDateTime.getTime()
        );
      });
      if (exact) return exact;
    }

    const selectedCreatedAt = new Date(assessment.createdAt);
    let closestScore = null;
    let minDiff = Infinity;

    if (!Number.isNaN(selectedCreatedAt.getTime())) {
      scores.forEach((score) => {
        const scoreCreatedAt = new Date(score.createdAt);
        if (Number.isNaN(scoreCreatedAt.getTime())) return;

        const diff = Math.abs(scoreCreatedAt - selectedCreatedAt);
        if (diff < minDiff) {
          minDiff = diff;
          closestScore = score;
        }
      });

      if (closestScore) return closestScore;
    }

    if (assessment.CompletedDate) {
      const sameDay = scores.find(
        (score) => score.CompletedDate === assessment.CompletedDate,
      );
      if (sameDay) return sameDay;
    }

    return closestScore;
  };

  const parseIsoDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const toMs = (value) => {
    const parsed = parseIsoDate(value);
    return parsed ? parsed.getTime() : null;
  };

  const toCompletedMs = (dateValue, timeValue) => {
    const parsed = normalizeDateTime(dateValue, timeValue);
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : null;
  };

  const toAssessmentLikeMs = (item) =>
    toCompletedMs(item?.CompletedDate, item?.CompletedTime) ??
    toMs(item?.updatedAt) ??
    toMs(item?.createdAt);

  const toLabLikeMs = (item) =>
    toCompletedMs(item?.CompletedDate, item?.CompletedTime) ??
    toMs(item?.updatedAt) ??
    toMs(item?.createdAt);

  const isMsWithinLinkIntervals = (ms) => {
    if (!Number.isFinite(ms)) return false;
    if (!Array.isArray(linkIntervals) || linkIntervals.length === 0)
      return false;

    return linkIntervals.some((interval) => {
      const startMs = toMs(interval?.start);
      const endMs = interval?.end ? toMs(interval.end) : null;
      if (!Number.isFinite(startMs)) return false;
      const effectiveEndMs = Number.isFinite(endMs)
        ? endMs
        : Number.POSITIVE_INFINITY;
      return ms >= startMs && ms <= effectiveEndMs;
    });
  };

  const isWithinLinkIntervals = (value) => isMsWithinLinkIntervals(toMs(value));

  useEffect(() => {
    let cancelled = false;

    async function loadLinkIntervals() {
      if (!connectedClient?.clientId) {
        setLinkIntervals([]);
        return;
      }

      setLinkIntervals(null);

      let professionalId = null;
      try {
        const attrs = await fetchUserAttributes();
        professionalId = attrs?.sub || null;
      } catch (e) {
        // ignore
      }
      if (!professionalId) {
        const user = await getCurrentUser().catch(() => null);
        professionalId = user?.userId || user?.username || user?.id || null;
      }

      if (!professionalId) {
        setLinkIntervals([]);
        return;
      }

      try {
        const res = await client.graphql({
          query: listUserLinks,
          variables: {
            filter: {
              professionalId: { eq: professionalId },
              clientId: { eq: connectedClient.clientId },
            },
            limit: 1000,
          },
        });

        const links = res?.data?.listUserLinks?.items || [];
        const intervals = links
          .map((link) => {
            const start = link?.linkedAt || link?.createdAt || null;
            const end = link?.unlinkedAt || null;
            return start ? { start, end } : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const dA = parseIsoDate(a.start) || new Date(0);
            const dB = parseIsoDate(b.start) || new Date(0);
            return dA - dB;
          });

        if (!cancelled) setLinkIntervals(intervals);
      } catch (err) {
        console.error("Failed to load UserLink intervals:", err);
        if (!cancelled) setLinkIntervals([]);
      }
    }

    loadLinkIntervals();
    return () => {
      cancelled = true;
    };
  }, [connectedClient?.clientId]);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        if (!connectedClient?.clientId) return;
        if (linkIntervals === null) return; // wait for link-window load

        if (!Array.isArray(linkIntervals) || linkIntervals.length === 0) {
          setSelectedFields({
            triggers: [],
            warningSigns: [],
            strengths: [],
            barriers: [],
          });
          return;
        }
        console.log(
          "Fetching CreatePlan for userId:",
          connectedClient.clientId,
        );

        const response = await client.graphql({
          query: listCreatePlans,
          variables: { filter: { userId: { eq: connectedClient.clientId } } },
        });

        console.log("GraphQL response:", response);

        const plansRaw = response?.data?.listCreatePlans?.items || [];
        const plans = plansRaw
          .filter((p) => isMsWithinLinkIntervals(toMs(p?.createdAt)))
          .sort(
            (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
          );

        const plan = plans[0];
        if (!plan) {
          console.log("No plan found for this user");
          return;
        }

        console.log("Fetched plan:", plan);

        const getChecked = (prefix, fields) => {
          const checked = fields.filter((f) => plan[f] === true);
          console.log(`Checked fields for ${prefix}:`, checked);
          return checked.map((f) => f.replace(prefix, ""));
        };

        const newSelectedFields = {
          triggers: getChecked("TG", [
            "TGBetrayal",
            "TGBoundaries",
            "TGDisorder",
            "TGPain",
            "TGRejection",
            "TGStress",
            "TGMemories",
            "TGUnjustTreatment",
            "TGUnpleasantConversations",
            "TGUnsafeEnvironments",
            "TGOther",
          ]),
          warningSigns: getChecked("WS", [
            "WSFlashbacks",
            "WSIncreasedAppetite",
            "WSDecreasedAppetite",
            "WSIntenseEmotions",
            "WSDisconnecting",
            "WSTime",
            "WSNegativeSelfTalk",
            "WSNeglectingSelfCare",
            "WSNightmares",
            "WSRecklessBehavior",
            "WSOther",
          ]),
          strengths: getChecked("PS", [
            "PSProblemSolving",
            "PSSelfEsteem",
            "PSChoiceAutonomy",
            "PSSelfCare",
            "PSSelfAwareness",
            "PSCreativity",
          ])
            .concat(
              getChecked("CS", [
                "CSForgiveness",
                "CSKindness",
                "CSGratitude",
                "CSPatience",
                "CSCourage",
              ]),
            )
            .concat(
              getChecked("CMS", [
                "CMSCopingChoice",
                "CMSResilience",
                "CMSSelfRegulation",
                "CMSOptimism",
              ]),
            )
            .concat(
              getChecked("COM", [
                "COMSupportSystem",
                "COMSocialIntelligence",
                "COMCommunication",
                "COMFaithCommunity",
                "COMAccomplishments",
              ]),
            ),
          barriers: getChecked("BR", [
            "BRStableHousing",
            "BRQualityHealthcare",
            "BRProfessionalAccess",
            "BRInsuranceCoverage",
            "BRFinancialConstraints",
            "BREducationAttainment",
            "BRSocialStigma",
            "BRUnsupportiveCommunity",
            "BRNonexistentCommunity",
            "BRCommunityResources",
            "BRProfessionalTrust",
            "BRHealthcareSystemTrust",
            "BRCognitiveImpairments",
            "BRDependentCare",
            "BRTransportation",
            "BRTechnologyAccess",
            "BRLanguage",
            "BRHealthcareProcess",
            "BROther",
            "BRResponseNote",
          ]),
        };

        console.log("Selected fields to set:", newSelectedFields);

        setSelectedFields(newSelectedFields);
      } catch (err) {
        console.error("Error fetching plan:", err);
      }
    };

    fetchPlan();
  }, [connectedClient?.clientId, linkIntervals]);
  // 🔹 Load lab results for the connected client
  useEffect(() => {
    async function loadLabs() {
      if (!connectedClient?.clientId) {
        setLabResults([]);
        setSelectedLab(null);
        setSelectedLabId(null);
        return;
      }

      if (linkIntervals === null) return; // wait

      if (!Array.isArray(linkIntervals) || linkIntervals.length === 0) {
        setLabResults([]);
        setSelectedLab(null);
        setSelectedLabId(null);
        return;
      }

      try {
        const res = await client.graphql({
          query: listLabResults,
          variables: { filter: { userId: { eq: connectedClient.clientId } } },
        });

        const itemsRaw = res.data?.listLabResults?.items || [];
        const items = itemsRaw.filter((l) =>
          isMsWithinLinkIntervals(toLabLikeMs(l)),
        );
        // newest first
        const sorted = [...items].sort(
          (a, b) => new Date(b.CompletedDate) - new Date(a.CompletedDate),
        );
        setLabResults(sorted);

        if (sorted.length > 0) {
          setSelectedLabId(sorted[0].id);
          setSelectedLab(sorted[0]);
        } else {
          setSelectedLabId(null);
          setSelectedLab(null);
        }
      } catch (error) {
        console.error("Failed to load lab results:", error);
        setLabResults([]);
        setSelectedLabId(null);
        setSelectedLab(null);
      }
    }

    loadLabs();
  }, [connectedClient?.clientId, linkIntervals]);
  // 🔹 Load scores for the two assessments being compared
  useEffect(() => {
    async function loadCompareScores() {
      if (!connectedClient?.clientId) {
        setCompareLeftScores(null);
        setCompareRightScores(null);
        return;
      }

      if (linkIntervals === null) return;
      if (!Array.isArray(linkIntervals) || linkIntervals.length === 0) {
        setCompareLeftScores(null);
        setCompareRightScores(null);
        return;
      }

      if (!compareLeftAssessmentId && !compareRightAssessmentId) {
        setCompareLeftScores(null);
        setCompareRightScores(null);
        return;
      }

      try {
        // Get all scores for this client once
        const res = await client.graphql({
          query: listAssessmentScores,
          variables: { filter: { userId: { eq: connectedClient.clientId } } },
        });

        const allScoresRaw = res.data?.listAssessmentScores?.items || [];
        const allScores = allScoresRaw.filter((s) =>
          isMsWithinLinkIntervals(toAssessmentLikeMs(s)),
        );

        const leftAssessment = assessments.find(
          (a) => a.id === compareLeftAssessmentId,
        );
        const rightAssessment = assessments.find(
          (a) => a.id === compareRightAssessmentId,
        );

        setCompareLeftScores(
          findBestScoreForAssessment(leftAssessment, allScores),
        );
        setCompareRightScores(
          findBestScoreForAssessment(rightAssessment, allScores),
        );
      } catch (error) {
        console.error("Failed to load compare assessment scores:", error);
        setCompareLeftScores(null);
        setCompareRightScores(null);
      }
    }

    loadCompareScores();
  }, [
    compareLeftAssessmentId,
    compareRightAssessmentId,
    assessments,
    connectedClient?.clientId,
    linkIntervals,
  ]);

  useEffect(() => {
    async function fetchHealthPlanReviewsForClient() {
      if (!connectedClient?.clientId) {
        setHealthPlanReviews([]);
        return;
      }

      if (linkIntervals === null) return;
      if (!Array.isArray(linkIntervals) || linkIntervals.length === 0) {
        setHealthPlanReviews([]);
        return;
      }

      try {
        const healthPlanResponse = await client.graphql({
          query: listCreatePlans,
          variables: { filter: { userId: { eq: connectedClient.clientId } } },
        });

        const healthPlansRaw =
          healthPlanResponse.data.listCreatePlans?.items || [];
        const healthPlans = healthPlansRaw
          .filter((p) => isMsWithinLinkIntervals(toMs(p?.createdAt)))
          .sort(
            (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
          );

        const healthPlanId = healthPlans.length > 0 ? healthPlans[0].id : null;
        console.log("Health Plan ID:", healthPlanId);

        if (!healthPlanId) {
          setHealthPlanReviews([]);
          return;
        }

        const reviewResponse = await client.graphql({
          query: listProfessionalReviews,
          variables: {
            filter: {
              ReviewedId: { eq: healthPlanId },
            },
          },
        });

        const reviews =
          reviewResponse.data.listProfessionalReviews?.items || [];
        setHealthPlanReviews(reviews);
      } catch (error) {
        console.error("Error fetching health plan reviews:", error);
        setHealthPlanReviews([]);
      }
    }

    fetchHealthPlanReviewsForClient();
  }, [viewFilter, connectedClient?.clientId, linkIntervals]);

  // filtered plans based on dates
  const [professionalReviews, setProfessionalReviews] = useState([]);
  useEffect(() => {
    async function fetchProfessionalReviews() {
      if (!selectedId || viewFilter !== "assessments") {
        setProfessionalReviews([]);
        return;
      }

      try {
        const response = await client.graphql({
          query: listProfessionalReviews,
          variables: {
            filter: {
              ReviewedId: { eq: selectedId },
            },
          },
        });

        const items = response.data.listProfessionalReviews?.items || [];
        setProfessionalReviews(items);
      } catch (error) {
        console.error("Error loading professional reviews:", error);
        setProfessionalReviews([]);
      }
    }

    fetchProfessionalReviews();
  }, [selectedId, viewFilter]);
  function openConfirmReview(id, type) {
    setPendingReview({ id, type });
    setConfirmReview(true);
  }
  async function handleConfirmReview() {
    if (!pendingReview.id || !pendingReview.type) {
      alert("No review selected.");
      setConfirmReview(false);
      return;
    }

    // Get user attributes
    const attrs = await fetchUserAttributes();
    const user = await getCurrentUser();
    const uid = attrs?.sub || user?.userId || user?.username || "";
    const normalizedProfessionalEmail = String(attrs?.email || "")
      .trim()
      .toLowerCase();
    const firstName = attrs["given_name"] || "";
    const lastName = attrs["family_name"] || "";
    const professionalName = `${firstName} ${lastName}`.trim() || "Unknown";

    // Get professional role from GeneralInformation
    const scoreResponse = await client.graphql({
      query: listGeneralInformations,
      variables: {
        filter: {
          or: [
            { owner: { eq: uid } },
            { userId: { eq: uid } },
            { Email: { eq: normalizedProfessionalEmail } },
          ],
        },
        limit: 200,
      },
    });

    const roleFromProfile =
      attrs["custom:professionalRole"] ||
      attrs["custom:role"] ||
      attrs["role"] ||
      "";
    let professionalRole = roleFromProfile || "Unknown Role";
    const items = scoreResponse?.data?.listGeneralInformations?.items || [];
    const matchingProfessionalItems = items.filter((item) => {
      const giUserId = String(item?.userId || "").trim();
      const giOwner = String(item?.owner || "").trim();
      const giEmail = String(item?.Email || "")
        .trim()
        .toLowerCase();
      return (
        (uid && (giUserId === uid || giOwner === uid)) ||
        (normalizedProfessionalEmail && giEmail === normalizedProfessionalEmail)
      );
    });

    const roleFromGeneralInfo = (
      matchingProfessionalItems.length ? matchingProfessionalItems : items
    )
      .map((item) => item?.ProfessionalRole)
      .find((role) => String(role || "").trim().length > 0);
    if (roleFromGeneralInfo) {
      professionalRole = roleFromGeneralInfo;
    }

    let reviewedId = pendingReview.id;

    // If reviewing a health plan, get the CreatePlan id for this user instead
    if (pendingReview.type === "health plan") {
      try {
        const userAttrs = await fetchUserAttributes();
        const responderId = userAttrs.sub || uid;
        const responderFirstName = userAttrs["given_name"] || "";
        const responderLastName = userAttrs["family_name"] || "";
        const responderEmail = String(userAttrs.email || "")
          .trim()
          .toLowerCase();

        // Update original notification status
        const userLinkResponse = await client.graphql({
          query: listUserLinks,
          variables: {
            filter: {
              clientId: { eq: connectedClient.clientId },
              professionalId: { eq: responderId },
            },
          },
        });

        const userLinks = userLinkResponse.data.listUserLinks?.items || [];
        const clientEmail =
          userLinks[0]?.clientEmail || connectedClient?.email || "";
        // Create RecommendationBack notification
        const newNote = {
          RecommendationNote: `${responderFirstName} ${responderLastName} (${professionalRole}) has finalized their review for your health plan`,

          SenderId: responderId,
          SenderFirstName: responderFirstName,
          SenderLastName: responderLastName,
          SenderEmail: responderEmail,
          TargetEmail: String(clientEmail || "")
            .trim()
            .toLowerCase(),
          TargetUserId: connectedClient?.clientId || null,
          NotificationType: "Reviewed",
          Status: "pending",
          StatusDate: new Date().toISOString().split("T")[0],
        };

        await client.graphql({
          query: createNotifications,
          variables: { input: newNote },
        });
      } catch (err) {
        console.error("Error sending update", err);
      }
      const planResponse = await client.graphql({
        query: listCreatePlans,
        variables: { filter: { userId: { eq: connectedClient.clientId } } },
      });
      const plansRaw = planResponse.data.listCreatePlans?.items || [];
      const plans = plansRaw
        .filter((p) => isWithinLinkIntervals(p?.createdAt))
        .sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
        );

      if (plans.length > 0) {
        reviewedId = plans[0].id;
      } else {
        alert("No health plan found for this user.");
        setConfirmReview(false);
        return;
      }
    } else {
      try {
        const userAttrs = await fetchUserAttributes();
        const responderId = userAttrs.sub || uid;
        const responderFirstName = userAttrs["given_name"] || "";
        const responderLastName = userAttrs["family_name"] || "";
        const responderEmail = String(userAttrs.email || "")
          .trim()
          .toLowerCase();

        // Update original notification status
        const userLinkResponse = await client.graphql({
          query: listUserLinks,
          variables: {
            filter: {
              clientId: { eq: connectedClient.clientId },
              professionalId: { eq: responderId },
            },
          },
        });

        const userLinks = userLinkResponse.data.listUserLinks?.items || [];
        const clientEmail =
          userLinks[0]?.clientEmail || connectedClient?.email || "";
        // Create RecommendationBack notification
        const newNote = {
          RecommendationNote: `${responderFirstName} ${responderLastName} (${professionalRole}) has finalized their review for your assessment`,

          SenderId: responderId,
          SenderFirstName: responderFirstName,
          SenderLastName: responderLastName,
          SenderEmail: responderEmail,
          TargetEmail: String(clientEmail || "")
            .trim()
            .toLowerCase(),
          TargetUserId: connectedClient?.clientId || null,
          NotificationType: "Reviewed",
          Status: "pending",
          StatusDate: new Date().toISOString().split("T")[0],
        };

        await client.graphql({
          query: createNotifications,
          variables: { input: newNote },
        });
      } catch (err) {
        console.error("Error sending update", err);
      }
    }

    const reviewDate = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const input = {
      ReviewedId: reviewedId,
      ReviewType: pendingReview.type,
      ProfessionalName: professionalName,
      ProfessionalRole: professionalRole,
      ReviewDate: reviewDate,
    };

    try {
      await client.graphql({
        query: createProfessionalReviews,
        variables: { input },
      });
      alert("Review finalized!");
    } catch (error) {
      console.error("Error creating review:", error);
      alert("Failed to finalize review.");
    } finally {
      setConfirmReview(false);
      setPendingReview({ id: null, type: null });
    }
  }

  const [pendingReview, setPendingReview] = useState({ id: null, type: null });
  const [confirmReview, setConfirmReview] = useState(false);

  useEffect(() => {
    if (!confirmReview || typeof document === "undefined") return;

    const updatePopupCenterX = () => {
      const rect = containerRef.current?.getBoundingClientRect?.();
      if (
        !rect ||
        !Number.isFinite(rect.left) ||
        !Number.isFinite(rect.width)
      ) {
        setPopupCenterX(Math.round(window.innerWidth / 2));
        return;
      }

      setPopupCenterX(Math.round(rect.left + rect.width / 2 + 16));
    };

    updatePopupCenterX();
    const intervalId = window.setInterval(updatePopupCenterX, 50);
    window.addEventListener("resize", updatePopupCenterX);

    return () => {
      window.removeEventListener("resize", updatePopupCenterX);
      window.clearInterval(intervalId);
    };
  }, [confirmReview]);

  const filteredPlans = trackPlans.filter((plan) => {
    const planDate =
      plan.CompletedDate ||
      (plan.createdAt ? String(plan.createdAt).slice(0, 10) : null);
    if (!planDate) return true;
    // direct string comparison
    return planDate >= startDate && planDate <= endDate;
  });
  // ==========================
  // 📤 Single Lab PDF download
  // ==========================
  const handleDownloadLabPdf = async () => {
    if (!selectedLab) {
      alert("No lab result selected to download.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Optional: load logo like your other PDFs
    const loadImageAsBase64 = (url, width, height) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width || img.width;
          canvas.height = height || img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (err) => reject(err);
        img.src = url;
      });
    };

    let logoBase64 = null;
    try {
      logoBase64 = await loadImageAsBase64(
        "/Outcomes-Excellence-Logo-Badge-Grow-White.png",
        150,
        50,
      );
    } catch (err) {
      console.error("Logo load failed:", err);
    }

    // Header bar
    const headerHeight = 50;
    doc.setFillColor("#0067B3");
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    if (logoBase64) {
      const logoWidth = 20;
      const logoHeight = 20;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 10;
      doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
    }

    const title = "Lab Results";
    doc.setTextColor("#ffffff");
    doc.setFontSize(18);
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, headerHeight - 8);

    // Client + date info
    let y = headerHeight + 15;
    doc.setFontSize(12);
    doc.setTextColor("#0067B3");

    const labDate =
      selectedLab.CompletedDate ||
      (selectedLab.createdAt
        ? new Date(selectedLab.createdAt).toISOString().split("T")[0]
        : "N/A");

    [
      `Client Name: ${connectedClient.firstName} ${connectedClient.lastName}`,
      `Lab Date: ${labDate}`,
    ].forEach((line) => {
      doc.text(line, 20, y);
      y += 8;
    });

    y += 6;

    // Group by category using the same logic as the on-screen UI
    const grouped = {
      Lipids: [],
      Minerals: [],
      Vitamins: [],
      Other: [],
    };

    labsFields.forEach((field) => {
      const raw = selectedLab[field.key];
      if (raw === null || raw === undefined || raw === "") return;

      const display = raw.toString();
      const category = getLabCategory(field.key);
      grouped[category].push({
        field,
        display,
      });
    });

    // Build rows for autoTable
    const rows = [];
    const sectionOrder = ["Lipids", "Minerals", "Vitamins", "Other"];

    sectionOrder.forEach((section) => {
      const items = grouped[section];
      if (!items || items.length === 0) return;

      // Section header row (spans columns in UI sense – here we just mark it)
      rows.push([`— ${section} —`, "", ""]);

      items.forEach(({ field, display }) => {
        rows.push([field.label, display, field.rangeText || ""]);
      });

      // Blank row between sections
      rows.push(["", "", ""]);
    });

    autoTable(doc, {
      startY: y,
      head: [["Test", "Result", "Reference Range"]],
      body: rows,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [0, 103, 179], textColor: 255 },
      margin: { left: 20, right: 20 },
      theme: "grid",
    });

    const filename = `LabResults-${
      labDate && labDate !== "N/A" ? labDate : "client"
    }.pdf`;
    doc.save(filename);
  };

  const handleDownloadComparePdf = () => {
    if (compareMode === "assessments") {
      if (!compareLeftAssessmentId || !compareRightAssessmentId) {
        alert("Select both assessments to export a comparison PDF.");
        return;
      }
    } else if (compareMode === "labs") {
      if (!compareLeftLabId || !compareRightLabId) {
        alert("Select both lab entries to export a comparison PDF.");
        return;
      }
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const comparisonTitle =
      compareMode === "labs"
        ? "Lab Comparison Report"
        : "Wholistic Health Assessment © Comparison Report";

    doc.setFontSize(16);
    doc.text(comparisonTitle, pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(11);

    const clientLine = `Client: ${connectedClient.firstName} ${connectedClient.lastName}`;
    doc.text(clientLine, 20, 30);

    if (compareMode === "assessments") {
      const left = assessments.find((a) => a.id === compareLeftAssessmentId);
      const right = assessments.find((a) => a.id === compareRightAssessmentId);
      if (!left || !right || !compareLeftScores || !compareRightScores) {
        alert("Could not load assessment data for export.");
        return;
      }

      const leftHeader =
        left.CompletedDate && left.CompletedTime
          ? `${left.CompletedDate} at ${left.CompletedTime}`
          : left.CompletedDate || "Assessment A";

      const rightHeader =
        right.CompletedDate && right.CompletedTime
          ? `${right.CompletedDate} at ${right.CompletedTime}`
          : right.CompletedDate || "Assessment B";

      const domains = [
        { key: "CMScore", label: "Coping Mechanisms" },
        { key: "MHScore", label: "Mental Health" },
        { key: "PHScore", label: "Physical Health" },
        { key: "SHScore", label: "Spiritual Health" },
        { key: "SoHScore", label: "Social Health" },
        { key: "TRScore", label: "Trauma" },
      ];

      const rows = domains.map(({ key, label }) => {
        const leftVal = compareLeftScores?.[key] ?? "—";
        const rightVal = compareRightScores?.[key] ?? "—";

        const leftNum = parseFloat(leftVal);
        const rightNum = parseFloat(rightVal);
        let diff = "—";
        if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum)) {
          const d = +(rightNum - leftNum).toFixed(2);
          diff = d > 0 ? `+${d}` : `${d}`;
          if (d === 0) diff = "0";
        }

        return [label, `${leftVal}`, `${rightVal}`, diff];
      });

      autoTable(doc, {
        startY: 40,
        head: [["Domain", leftHeader, rightHeader, "Difference"]],
        body: rows,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [0, 103, 179], textColor: 255 },
        margin: { left: 20, right: 20 },
        theme: "grid",
        columnStyles: {
          3: {
            halign: "left",
            cellPadding: { top: 3, right: 3, bottom: 3, left: 10 },
          },
        },
        didParseCell: (data) => {
          if (data.column.index === 3 && data.section === "body") {
            const rowIndex = data.row.index;
            const domain = domains[rowIndex];
            const leftVal = compareLeftScores?.[domain.key];
            const rightVal = compareRightScores?.[domain.key];

            if (
              leftVal !== null &&
              leftVal !== undefined &&
              rightVal !== null &&
              rightVal !== undefined
            ) {
              const diff = parseFloat(rightVal) - parseFloat(leftVal);

              if (diff > 1.99) {
                data.cell.styles.textColor = [176, 0, 0];
              } else if (diff < -1.99) {
                data.cell.styles.textColor = [40, 167, 69];
              } else {
                data.cell.styles.textColor = [192, 192, 192];
              }
            }
          }
        },
        didDrawCell: (data) => {
          if (data.column.index === 3 && data.section === "body") {
            const rowIndex = data.row.index;
            const domain = domains[rowIndex];
            const leftVal = compareLeftScores?.[domain.key];
            const rightVal = compareRightScores?.[domain.key];

            if (
              leftVal !== null &&
              leftVal !== undefined &&
              rightVal !== null &&
              rightVal !== undefined
            ) {
              const diff = parseFloat(rightVal) - parseFloat(leftVal);

              const circleX = data.cell.x + 4;
              const circleY = data.cell.y + data.cell.height / 2;
              const radius = 2;

              if (diff > 1.99) {
                doc.setFillColor(176, 0, 0);
                doc.circle(circleX, circleY, radius, "F");
              } else if (diff < -1.99) {
                doc.setFillColor(40, 167, 69);
                doc.circle(circleX, circleY, radius, "F");
              } else {
                doc.setFillColor(192, 192, 192);
                doc.circle(circleX, circleY, radius, "F");
              }
            }
          }
        },
      });
    } else {
      // compareMode === "labs"
      const leftLab = labResults.find((l) => l.id === compareLeftLabId);
      const rightLab = labResults.find((l) => l.id === compareRightLabId);
      if (!leftLab || !rightLab) {
        alert("Could not load lab data for export.");
        return;
      }

      const leftHeader = leftLab.CompletedDate || "Lab A";
      const rightHeader = rightLab.CompletedDate || "Lab B";

      const rows = labsFields.map(({ key, label }) => {
        const leftRaw = leftLab[key];
        const rightRaw = rightLab[key];

        const leftDisplay =
          leftRaw === null || leftRaw === undefined || leftRaw === ""
            ? "—"
            : leftRaw.toString();
        const rightDisplay =
          rightRaw === null || rightRaw === undefined || rightRaw === ""
            ? "—"
            : rightRaw.toString();

        const leftNum = parseFloat(leftRaw);
        const rightNum = parseFloat(rightRaw);
        let diff = "—";
        if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum)) {
          const d = +(rightNum - leftNum).toFixed(2);
          diff = d > 0 ? `+${d}` : `${d}`;
          if (d === 0) diff = "0";
        }

        return [label, leftDisplay, rightDisplay, diff];
      });

      autoTable(doc, {
        startY: 40,
        head: [["Test", leftHeader, rightHeader, "Difference"]],
        body: rows,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [0, 103, 179], textColor: 255 },
        margin: { left: 20, right: 20 },
        theme: "grid",
      });
    }

    doc.save("ComparisonReport.pdf");
  };

  function RadialScoreChart({
    scores,
    thresholds = categoryThresholds,
    size = isMobile ? 200 : 400,
  }) {
    const center = size / 2;
    const radiusStep = isMobile ? 20 : 40;

    const categories = [
      { key: "SHScore", label: "Spiritual" },
      { key: "CMScore", label: "Coping" },
      { key: "SoHScore", label: "Social" },
      { key: "TRScore", label: "Trauma" },
      { key: "PHScore", label: "Physical" },
      { key: "MHScore", label: "Mental" },
    ];

    const sliceAngle = 360 / categories.length;
    const ringColors = ["#28a745", "#ffc107", "#B00000"];

    // Helper to describe arcs for rings (outerRadius added for thickness)
    function describeArc(
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
    ) {
      const rad = (deg) => (deg * Math.PI) / 180;

      // Points for outer arc
      const x1 = cx + outerRadius * Math.cos(rad(startAngle));
      const y1 = cy + outerRadius * Math.sin(rad(startAngle));
      const x2 = cx + outerRadius * Math.cos(rad(endAngle));
      const y2 = cy + outerRadius * Math.sin(rad(endAngle));

      // Points for inner arc
      const x3 = cx + innerRadius * Math.cos(rad(endAngle));
      const y3 = cy + innerRadius * Math.sin(rad(endAngle));
      const x4 = cx + innerRadius * Math.cos(rad(startAngle));
      const y4 = cy + innerRadius * Math.sin(rad(startAngle));

      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

      return [
        `M ${x1} ${y1}`,
        `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
        "Z",
      ].join(" ");
    }

    // Function to get threshold index for a score and category
    const getThresholdIndex = (score, categoryKey) => {
      const t = thresholds[categoryKey];
      if (!t) return -1;
      for (let i = 0; i < t.length; i++) {
        if (score >= t[i].min && score <= t[i].max) return i;
      }
      return -1;
    };

    return (
      <svg
        width="100%"
        viewBox={`0 0 ${size} ${size}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          background: "transparent",
          width: "100%",
          height: "auto",
          maxWidth: size,
        }}
      >
        <g transform={`rotate(-30 ${center} ${center})`}>
          {/* Colored background split into pie slices */}
          {categories.map((cat, i) => {
            const startAngle = i * sliceAngle;
            const endAngle = startAngle + sliceAngle;
            const innerRadius = 0; // start from center (full circle)
            const outerRadius = radiusStep * 3; // covers the 3 rings (green, yellow, red)

            return (
              <path
                key={`bg-slice-${i}`}
                d={describeArc(
                  center,
                  center,
                  innerRadius,
                  outerRadius,
                  startAngle,
                  endAngle,
                )}
                fill="rgba(235, 235, 235, 1)" // light orange color
                stroke="white"
                strokeWidth={2}
              />
            );
          })}

          {/* Your existing rings per slice */}
          {categories.map((cat, i) => {
            const score = scores?.[cat.key] ?? 0;
            const thresholdIndex = getThresholdIndex(score, cat.key);

            const rings = [];
            for (let ring = 0; ring <= thresholdIndex; ring++) {
              const innerRadius = radiusStep * ring;
              const outerRadius = radiusStep * (ring + 1);

              const startAngle = i * sliceAngle;
              const endAngle = startAngle + sliceAngle;

              rings.push(
                <path
                  key={`${cat.key}-ring-${ring}`}
                  d={describeArc(
                    center,
                    center,
                    innerRadius,
                    outerRadius,
                    startAngle,
                    endAngle,
                  )}
                  fill={ringColors[ring]}
                  stroke="white"
                  strokeWidth="2"
                />,
              );
            }
            return rings;
          })}

          {/* NEW 4th ring: full grey circle outside all others */}
          {/* Grey ring split into 6 slices with white stroke dividers */}
          {categories.map((cat, i) => {
            const startAngle = i * sliceAngle;
            const endAngle = startAngle + sliceAngle;
            const innerRadius = radiusStep * 3.5 - 15; // slightly smaller to fit stroke nicely
            const outerRadius = radiusStep * 3.5 + 15; // match strokeWidth of 30

            return (
              <path
                key={`grey-ring-slice-${i}`}
                d={describeArc(
                  center,
                  center,
                  innerRadius,
                  outerRadius,
                  startAngle,
                  endAngle,
                )}
                fill="#888888"
                stroke="white"
                strokeWidth={2}
              />
            );
          })}
          {categories.map((cat, i) => {
            const startAngle = i * sliceAngle;
            const endAngle = startAngle + sliceAngle;

            // Text radius: halfway through the grey ring thickness
            const textRadius = radiusStep * 3.4;

            // Create unique path id
            const pathId = `textPath-${cat.key}`;

            return (
              <g key={`text-label-${cat.key}`}>
                {/* Define path for text */}
                <path
                  id={pathId}
                  d={describeTextArc(
                    center,
                    center,
                    textRadius,
                    startAngle + 5,
                    endAngle - 5,
                  )} // small padding inside slice angle
                  fill="none"
                />
                <text
                  fill="#ffffff"
                  fontSize="16"
                  fontWeight="Normal"
                  textAnchor="middle"
                >
                  <textPath href={`#${pathId}`} startOffset="50%">
                    {cat.label}
                  </textPath>
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    );
  }
  function RadialScoreChartWrapper(props) {
    const svgRef = useRef();

    // Pass ref to your RadialScoreChart component
    return <RadialScoreChart {...props} svgRef={svgRef} />;
  }

  function WholeHealthReportDonut({ scores }) {
    const dimensions = [
      { key: "MHScore", label: "Mental" },
      { key: "PHScore", label: "Physical" },
      { key: "SHScore", label: "Spiritual" },
      { key: "SoHScore", label: "Social" },
    ].map((dimension) => {
      const score = Number(scores?.[dimension.key]) || 0;
      const status =
        score <= categoryThresholds[dimension.key][0].max
          ? "Strong"
          : score <= categoryThresholds[dimension.key][1].max
            ? "Opportunity"
            : "Attention Needed";
      return { ...dimension, status };
    });
    const weight = { Strong: 1, Opportunity: 1.5, "Attention Needed": 2 };
    const color = {
      Strong: "#3B7D23",
      Opportunity: "#D39E00",
      "Attention Needed": "#B00000",
    };
    const total = dimensions.reduce(
      (sum, dimension) => sum + weight[dimension.status],
      0,
    );
    const point = (radius, angle) => ({
      x: 150 + radius * Math.cos((angle * Math.PI) / 180),
      y: 150 + radius * Math.sin((angle * Math.PI) / 180),
    });
    const path = (start, end) => {
      const gap = 7,
        trimAngle = (radius) => (Math.asin(gap / 2 / radius) * 180) / Math.PI,
        outerTrim = trimAngle(132),
        innerTrim = trimAngle(78),
        outerStart = point(132, start + outerTrim),
        outerEnd = point(132, end - outerTrim),
        innerEnd = point(78, end - innerTrim),
        innerStart = point(78, start + innerTrim),
        arc = end - start > 180 ? 1 : 0;
      return `M ${outerStart.x} ${outerStart.y} A 132 132 0 ${arc} 1 ${outerEnd.x} ${outerEnd.y} L ${innerEnd.x} ${innerEnd.y} A 78 78 0 ${arc} 0 ${innerStart.x} ${innerStart.y} Z`;
    };
    let accumulated = 0;
    return (
      <svg
        viewBox="0 0 300 300"
        role="img"
        aria-label="Whole Health Picture"
        style={{
          display: "block",
          margin: "0 auto",
          maxWidth: 300,
          width: "100%",
        }}
      >
        {dimensions.map((dimension) => {
          const portion = weight[dimension.status] / total,
            start = -90 + accumulated * 360;
          accumulated += portion;
          const end = -90 + accumulated * 360,
            label = point(105, (start + end) / 2);
          return (
            <g key={dimension.key}>
              <path
                d={path(start, end)}
                fill={color[dimension.status]}
                stroke="#F5F3EF"
                strokeWidth="3"
              />
              <text
                x={label.x}
                y={label.y}
                fill="#F5F3EF"
                fontSize="10"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {dimension.label}
              </text>
            </g>
          );
        })}
        <circle cx="150" cy="150" r="78" fill="#F0F4FB" />
        <text
          x="150"
          y="143"
          fill="#003366"
          fontSize="11"
          fontWeight="800"
          textAnchor="middle"
        >
          WHOLE
        </text>
        <text
          x="150"
          y="163"
          fill="#003366"
          fontSize="11"
          fontWeight="800"
          textAnchor="middle"
        >
          HEALTH
        </text>
      </svg>
    );
  }

  function WholeHealthReportLegend() {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          justifyContent: "center",
          marginTop: 12,
        }}
      >
        {[
          { color: "#3B7D23", label: "Strong" },
          { color: "#D39E00", label: "Opportunity" },
          { color: "#B00000", label: "Attention Needed" },
          { color: "#9CA3AF", label: "Not Factored" },
        ].map((item) => (
          <span
            key={item.label}
            style={{
              alignItems: "center",
              color: "#003366",
              display: "inline-flex",
              fontSize: 13,
              fontWeight: 600,
              gap: 6,
            }}
          >
            <i
              style={{
                background: item.color,
                borderRadius: "50%",
                height: 10,
                width: 10,
              }}
            />
            {item.label}
          </span>
        ))}
        <span style={{ color: "#6B7280", fontSize: 12, width: "100%" }}>
          Larger section = More attention needed
        </span>
      </div>
    );
  }
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    async function loadAssessmentScores() {
      const currentAssessment =
        assessments.find((assessment) => assessment.id === selectedId) ||
        selectedAssessment;

      if (!currentAssessment) {
        setAssessmentScores(null);
        return;
      }

      if (linkIntervals === null) return;
      if (!Array.isArray(linkIntervals) || linkIntervals.length === 0) {
        setAssessmentScores(null);
        return;
      }

      try {
        const res = await client.graphql({
          query: listAssessmentScores,
          variables: {
            filter: {
              or: [
                { owner: { eq: currentAssessment.userId } },
                { userId: { eq: currentAssessment.userId } },
              ],
            },
          },
        });

        const allScoresRaw = res.data?.listAssessmentScores?.items || [];
        const allScores = allScoresRaw.filter((s) =>
          isMsWithinLinkIntervals(toAssessmentLikeMs(s)),
        );
        setAssessmentScores(
          findBestScoreForAssessment(currentAssessment, allScores),
        );
      } catch (error) {
        console.error("Failed to load assessment scores:", error);
        setAssessmentScores(null);
      }
    }

    loadAssessmentScores();
  }, [
    selectedId,
    selectedAssessment,
    assessments,
    connectedClient?.clientId,
    linkIntervals,
  ]);
  useEffect(() => {
    async function loadData() {
      try {
        if (!connectedClient?.clientId) return;

        if (linkIntervals === null) return;
        if (!Array.isArray(linkIntervals) || linkIntervals.length === 0) {
          setAssessments([]);
          setSelectedId(null);
          setSelectedAssessment(null);
          setTrackPlans([]);
          return;
        }

        await getCurrentUser().catch(() => null);

        // Load assessments (replace with your actual query or import)
        const assessmentResult = await client.graphql({
          query: listAssessmentAnswers, // make sure this is imported or defined
          variables: {
            filter: {
              userId: { eq: connectedClient.clientId },
              isActive: { eq: false },
            },
          },
        });

        const assessmentsRaw =
          assessmentResult.data.listAssessmentAnswers.items;
        const allowedAssessments = (assessmentsRaw || []).filter((a) =>
          isMsWithinLinkIntervals(toAssessmentLikeMs(a)),
        );
        const sortedAssessments = [...allowedAssessments].sort(
          (a, b) => new Date(b.CompletedDate) - new Date(a.CompletedDate),
        );
        setAssessments(sortedAssessments);

        if (sortedAssessments.length > 0) {
          setSelectedId(sortedAssessments[0].id);
          setSelectedAssessment(sortedAssessments[0]);
        }

        // Load track plans with your custom query
        const trackResult = await client.graphql({
          query: LIST_TRACK_PLANS_CUSTOM,
          variables: { filter: { userId: { eq: connectedClient.clientId } } },
        });

        const trackPlansRaw = trackResult.data.listTrackPlans.items || [];
        const allowedTrackPlans = trackPlansRaw.filter((p) =>
          isMsWithinLinkIntervals(toMs(p?.createdAt)),
        );
        setTrackPlans(allowedTrackPlans); // ← No more filtering by status
      } catch (error) {
        console.error("Error loading data:", error);
        alert(
          "Failed to load data: " +
            (error?.errors?.[0]?.message || error.message || "Unknown error"),
        );
      }
    }

    loadData();
  }, [connectedClient?.clientId, linkIntervals]);

  const handleViewToggle = (view) => {
    setViewFilter(view);
  };
  function svgToPng(svgElement, width, height) {
    return new Promise((resolve) => {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = url;
    });
  }

  const handleChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    const found = assessments.find((a) => a.id === id);
    setSelectedAssessment(found);
  };
  const trackPlanRef = useRef(null);

  const downloadTrackPlansPDF = async (includePercentages = true) => {
    if (!trackPlans || trackPlans.length === 0) return;

    const doc = new jsPDF();
    const loadImageAsBase64 = (url, width, height) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width || img.width;
          canvas.height = height || img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (err) => reject(err);
        img.src = url;
      });
    };

    let logoBase64 = null;
    try {
      logoBase64 = await loadImageAsBase64(
        "/Outcomes-Excellence-Logo-Badge-Grow-White.png",
        150,
        50,
      );
    } catch (err) {
      console.error("Logo load failed:", err);
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const headerHeight = 50;
    doc.setFillColor("#0067B3");
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    if (logoBase64) {
      const logoWidth = 20; // increase for less blur
      const logoHeight = 20;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 10; // top margin
      doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
    }
    let y = headerHeight + 15;
    // ===== Title under logo =====
    const title = "Health Plan";
    doc.setTextColor("#ffffff"); // match your blue text
    doc.setFontSize(18);
    const titleWidth = doc.getTextWidth(title);

    doc.text(title, (pageWidth - titleWidth) / 2, headerHeight - 8);

    const addSelectedFields = (fieldsObj) => {
      const sections = [
        { label: "Triggers", items: fieldsObj.triggers },
        { label: "Warning Signs", items: fieldsObj.warningSigns },
        { label: "Strengths", items: fieldsObj.strengths },
        { label: "Barriers", items: fieldsObj.barriers },
      ];

      doc.setFontSize(12);
      doc.setTextColor("#0067B3");

      // --- Client name + current date ---
      const clientNameLine = `Client Name: ${connectedClient.firstName} ${connectedClient.lastName}`;
      const today = new Date();
      const currentDateLine = `Date: ${today.getFullYear()}-${String(
        today.getMonth() + 1,
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      [clientNameLine, currentDateLine].forEach((line) => {
        doc.text(line, 20, y);
        y += 8;
      });

      y += 10;

      sections.forEach((section) => {
        // Section label in blue
        doc.setFontSize(12);
        doc.setTextColor("#0067B3");
        doc.text(`${section.label}:`, 20, y);
        y += 6;

        // Section items in black
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);

        if (section.items.length > 0) {
          section.items.forEach((item) => {
            const lines = doc.splitTextToSize(`- ${item}`, 170);
            lines.forEach((line) => {
              doc.text(line, 25, y);
              y += 5;
            });
          });
        } else {
          doc.text("None selected", 25, y);
          y += 5;
        }

        y += 4; // spacing after each section
      });

      y += 8; // extra spacing before plans start
    };

    addSelectedFields(selectedFields);

    // then render them

    const addPlan = (plan, index) => {
      doc.setFontSize(14);
      doc.setTextColor("#0067B3");
      doc.text(`${index + 1}. ${plan.Action}`, 20, y);
      y += 8;

      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);

      const details = [
        { label: "Question", value: plan.ActionQuestion },

        { label: "Start Date", value: plan.CompletedDate || "Unknown" },
        { label: "Frequency", value: plan.Frequency },
        { label: "Note", value: plan.ActionNote },
      ];
      if (plan.Discontinued === false && plan.Successful === false) {
        doc.setTextColor("#0067B3"); // 🔴 Red
        doc.text(`In Progress`, 25, y);
        y += 6;
      }

      if (plan.Discontinued === true && plan.DiscontinuedDate) {
        doc.setTextColor(204, 68, 68); // 🔴 Red
        doc.text(`Discontinued: ${plan.DiscontinuedDate}`, 25, y);
        y += 6;
      }

      if (plan.Successful === true && plan.SuccessfulDate) {
        doc.setTextColor(34, 139, 34); // 🟢 Green
        doc.text(`Successful: ${plan.SuccessfulDate}`, 25, y);
        y += 6;
      }

      doc.setTextColor(0, 0, 0); // reset to black

      details.forEach(({ label, value }) => {
        const lines = doc.splitTextToSize(`${label}: ${value}`, 170);
        lines.forEach((line) => {
          doc.text(line, 25, y);
          y += 6;
        });
      });

      if (includePercentages) {
        // WEEKLY TABLE
        const weekRows = [];
        for (let i = 1; i <= 8; i++) {
          const date =
            plan[`Update${i}Date`] != null ? plan[`Update${i}Date`] : "N/A";
          const note =
            plan[`Update${i}Note`] != null
              ? plan[`Update${i}Note`]
              : "No note provided.";
          weekRows.push([`Update ${i}`, date, note]);
        }

        autoTable(doc, {
          startY: y + 4,
          head: [["Update", "Date", "Note"]],
          body: weekRows,
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fillColor: [0, 103, 179], textColor: 255 },
          margin: { left: 20, right: 20 },
          theme: "grid",
          columnStyles: {
            0: { cellWidth: 25 }, // "Update"
            1: { cellWidth: 40 }, // "Date"
            2: { cellWidth: 105 }, // "Note" — takes more space
          },
        });

        y = doc.lastAutoTable.finalY + 10;
        const percent = Math.min(plan.Percentage || 0, 100);
        doc.setDrawColor(0, 103, 179);
        doc.setFillColor(0, 103, 179);
        doc.rect(25, y + 4, (percent / 100) * 150, 6, "F");
        doc.setDrawColor(150);
        doc.rect(25, y + 4, 150, 6);
        doc.text(`Percentage: ${percent}%`, 25, y + 16);
        y += 34;
      } else {
        y += 10;
      }

      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    };

    trackPlans.forEach((plan, index) => addPlan(plan, index));
    if (healthPlanReviews && healthPlanReviews.length > 0) {
      if (y > 270) {
        // page break if near bottom
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor("#0067B3");
      doc.text("Health Plan Reviews", 20, y);
      y += 10;

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);

      healthPlanReviews.forEach((review) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }

        doc.text(
          `Professional Name: ${review.ProfessionalName || "N/A"}`,
          25,
          y,
        );
        y += 7;
        doc.text(
          `Professional Role: ${review.ProfessionalRole || "N/A"}`,
          25,
          y,
        );
        y += 7;
        doc.text(`Date: ${review.ReviewDate || "N/A"}`, 25, y);
        y += 7;

        y += 10;
      });
    }
    doc.save("Track_Plans.pdf");
  };
  const handleDownloadPdf = async () => {
    if (!selectedAssessment) return;

    // --- Grab the radial chart as PNG ---
    const svgElement = document.querySelector("#radialChartContainer svg");
    let radialChartImgData = null;
    if (svgElement) {
      radialChartImgData = await new Promise((resolve) => {
        svgToPngBase64(svgElement, 400, 400, (pngBase64) => {
          resolve(pngBase64 || null);
        });
      });
    }

    // --- Load the logo from /public folder ---
    const loadImageAsBase64 = (url, width, height) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width || img.width;
          canvas.height = height || img.height;
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (err) => reject(err);
        img.src = url;
      });
    };

    let logoBase64 = null;
    try {
      logoBase64 = await loadImageAsBase64(
        "/Outcomes-Excellence-Logo-Badge-Grow-White.png",
      );
    } catch (err) {
      console.error("Logo load failed:", err);
    }

    // --- Helpers for colors ---
    const getDomainColor = (score, domainKey) => {
      const t = categoryThresholds[domainKey];
      if (!t) return "gray";
      for (const threshold of t) {
        if (score >= threshold.min && score <= threshold.max)
          return threshold.color;
      }
      return "gray";
    };

    const getQuestionColor = (value) => {
      if (value === undefined || value === null) return "gray";
      if (typeof value === "string") return "black";
      if (value >= 0 && value <= 1) return "#28a745"; // green
      if (value === 2) return "#ffc107"; // yellow
      if (value >= 3 && value <= 4) return "#B00000"; // red
      return "gray";
    };

    // --- PDF setup ---
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header
    const headerHeight = 50;
    doc.setFillColor("#0067B3");
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    if (logoBase64) {
      const logoWidth = 24;
      const logoHeight = 24;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 8;
      doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
    }

    // ===== Title under logo =====
    const title = "Assessment Results";
    doc.setTextColor("#ffffff"); // match your blue text
    doc.setFontSize(18);
    const titleWidth = doc.getTextWidth(title);

    doc.text(title, (pageWidth - titleWidth) / 2, headerHeight - 8);

    // Info section
    let y = headerHeight + 15;
    doc.setFontSize(12);
    doc.setTextColor("#0067B3");

    const rawDate = selectedAssessment?.createdAt || selectedAssessment?.date;

    const assessmentDate = rawDate
      ? new Date(rawDate).toLocaleDateString() // formats as "MM/DD/YYYY" by default
      : "N/A";

    // then render them
    [
      `Client Name: ${connectedClient.firstName} ${connectedClient.lastName}`,
      `Assessment Date: ${assessmentDate}`,
    ].forEach((line) => {
      doc.text(line, 20, y);
      y += 8;
    });
    y += 10;

    // Radial chart image (centered)
    if (radialChartImgData) {
      const imgWidth = 90;
      const x = (pageWidth - imgWidth) / 2;
      doc.addImage(radialChartImgData, "PNG", x, y, imgWidth, 90);
      y += 100;
    }

    const checkPageBreak = () => {
      if (y > pageHeight - 30) {
        doc.addPage();
        y = 20;
      }
    };

    // --- Domains ---
    const domains = [
      {
        key: "CMScore",
        label: "Coping Mechanisms",
        questionKeys: [
          "CMAlcohol",
          "CMDrugs",
          "CMOvereating",
          "CMSmoking",
          "CMGaming",
          "CMShopping",
          "CMGambling",
          "CMSex",
          "CMPorn",
          "CMBlaming",
          "CMHurting",
          "CMDisengage",
          "CMArt",
          "CMMusic",
          "CMPoetry",
          "CMReading",
          "CMGroups",
          "CMCounseling",
          "CMVenting",
          "CMWriting",
          "CMSensory",
          "CMDancing",
          "CMExercising",
          "CMWalking",
          "CMChange",
          "CMAnalyze",
          "CMDaydream",
          "CMPositive",
        ],
      },
      {
        key: "MHScore",
        label: "Mental Health Symptoms",
        questionKeys: [
          "MHSleeplessness",
          "MHAlcoholUse",
          "MHAnxiety",
          "MHDepression",
          "MHDrugUse",
          "MHGrief",
          "MHGuilt",
          "MHIrritability",
          "MHStress",
          "MHRegret",
          "MHSuicidalThoughts",
          "MHLoneliness",
          "MHWorry",
        ],
      },
      {
        key: "PHScore",
        label: "Physical Health Behaviors",
        questionKeys: [
          "PHVegeFruits",
          "PHBeanLentils",
          "PHGrainBreads",
          "PHDairy",
          "PHMeat",
          "PHFishSeafood",
          "PHSweets",
          "PHWater",
          "PHPhysicalActivity",
        ],
      },
      {
        key: "SHScore",
        label: "Spiritual Health",
        questionKeys: [
          "SHSpiritualDefine",
          "SHSpiritualIntegrate",
          "SHPrayer",
          "SHSpiritualActivity",
          "SHReadText",
          "SHAlignText",
          "SHCommunity",
        ],
      },
      {
        key: "SoHScore",
        label: "Social Health",
        questionKeys: [
          "SoHLearning",
          "SoHStable",
          "SoHSafety",
          "SoHKindness",
          "SoHForgiveness",
          "SoHPatience",
          "SoHRelationships",
          "SoHContribution",
          "SoHBoundaries",
          "SoHEUnpleasant",
          "SoHEPleasant",
          "SoHEControl",
        ],
      },
    ];

    // --- Loop over domains and questions ---
    domains.forEach(({ key, label, questionKeys }) => {
      const domainScore = assessmentScores[key];
      if (domainScore === undefined || domainScore === null) return;

      checkPageBreak();

      const domainColor = getDomainColor(domainScore, key);
      const circleX = 22;
      const circleRadius = 4;
      const textX = circleX + circleRadius * 2 + 1;

      doc.setDrawColor(0);
      doc.setFillColor(domainColor);
      doc.circle(circleX, y - 1, circleRadius, "F");

      doc.setFontSize(14);
      doc.setTextColor("#0067B3");
      doc.text(label, textX, y);
      y += 10;

      // Line under title
      doc.setDrawColor("#0067B3");
      doc.setLineWidth(0.8);
      doc.line(20, y - 4, 180, y - 4);
      y += 10;

      doc.setFontSize(11);
      doc.setTextColor("#333");

      questionKeys.forEach((qKey) => {
        const valAnswer = selectedAssessment[qKey];
        const valScore = assessmentScores[qKey];
        if (valAnswer === undefined || valAnswer === null) return;

        checkPageBreak();

        const qColor = getQuestionColor(valScore);
        doc.setDrawColor(0);
        doc.setFillColor(qColor);
        doc.circle(22, y - 1, 2, "F");

        const questionText = keyToQuestionMap?.[qKey] ?? qKey;
        const displayVal =
          typeof valAnswer === "string" ? valAnswer : valAnswer.toString();
        const text = `${questionText}: ${displayVal}`;

        const lines = doc.splitTextToSize(text, 160);
        lines.forEach((line, i) => {
          doc.text(line, 30, y + i * 2);
        });
        y += 3 * lines.length + 4;
      });

      y += 12;
    });
    if (professionalReviews && professionalReviews.length > 0) {
      if (y > 270) {
        // page break if near bottom
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor("#0067B3");
      doc.text("Assessment Reviews", 20, y);
      y += 10;

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);

      professionalReviews.forEach((review) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }

        doc.text(
          `Professional Name: ${review.ProfessionalName || "N/A"}`,
          25,
          y,
        );
        y += 7;
        doc.text(
          `Professional Role: ${review.ProfessionalRole || "N/A"}`,
          25,
          y,
        );
        y += 7;
        doc.text(`Date: ${review.ReviewDate || "N/A"}`, 25, y);
        y += 7;

        y += 10;
      });
    }
    doc.save("AssessmentResults.pdf");
  };

  function svgToPngBase64(svgElement, width, height, callback) {
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      // clear canvas
      ctx.clearRect(0, 0, width, height);
      // Draw white background (optional)
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, width, height);
      // Draw the SVG image
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      const pngBase64 = canvas.toDataURL("image/png");
      callback(pngBase64);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      callback(null);
    };
    img.src = url;
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 5,
        width: 350,
        minWidth: 300,

        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",

        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <button
          onClick={() => handleViewToggle("assessments")}
          type="button"
          style={{
            padding: "10px 24px",
            borderRadius: 12,
            border:
              viewFilter === "assessments"
                ? "2px solid #0067B3"
                : "2px solid #ccc",
            backgroundColor: viewFilter === "assessments" ? "#d9eaff" : "#fff",
            color: viewFilter === "assessments" ? "#0067B3" : "#333",
            fontWeight: "600",
            cursor: "pointer",
            minWidth: 160,
            transition: "all 0.3s ease",
          }}
          aria-pressed={viewFilter === "assessments"}
        >
          Assessments
        </button>

        <button
          onClick={() => handleViewToggle("trackPlans")}
          type="button"
          style={{
            padding: "10px 24px",
            borderRadius: 12,
            border:
              viewFilter === "trackPlans"
                ? "2px solid #0067B3"
                : "2px solid #ccc",
            backgroundColor: viewFilter === "trackPlans" ? "#d9eaff" : "#fff",
            color: viewFilter === "trackPlans" ? "#0067B3" : "#333",
            fontWeight: "600",
            cursor: "pointer",
            minWidth: 160,
            transition: "all 0.3s ease",
          }}
          aria-pressed={viewFilter === "trackPlans"}
        >
          Health Plans
        </button>

        {/* NEW: Labs button */}
        <button
          onClick={() => handleViewToggle("labs")}
          type="button"
          style={{
            padding: "10px 24px",
            borderRadius: 12,
            border:
              viewFilter === "labs" ? "2px solid #0067B3" : "2px solid #ccc",
            backgroundColor: viewFilter === "labs" ? "#d9eaff" : "#fff",
            color: viewFilter === "labs" ? "#0067B3" : "#333",
            fontWeight: "600",
            cursor: "pointer",
            minWidth: 160,
            transition: "all 0.3s ease",
          }}
          aria-pressed={viewFilter === "labs"}
        >
          Lab Results
        </button>
        {/* 🔹 NEW: Compare */}
        <button
          onClick={() => handleViewToggle("compare")}
          type="button"
          style={{
            padding: "10px 24px",
            borderRadius: 12,
            border:
              viewFilter === "compare" ? "2px solid #0067B3" : "2px solid #ccc",
            backgroundColor: viewFilter === "compare" ? "#d9eaff" : "#fff",
            color: viewFilter === "compare" ? "#0067B3" : "#333",
            fontWeight: "600",
            cursor: "pointer",
            minWidth: 160,
            transition: "all 0.3s ease",
          }}
          aria-pressed={viewFilter === "compare"}
        >
          Compare
        </button>
      </div>

      <h2
        style={{
          textAlign: "center",
          color: "#0067B3",
          marginBottom: 30,
          fontWeight: "700",
          fontSize: "2rem",
        }}
      >
        {viewFilter === "assessments"
          ? "View Assessments"
          : viewFilter === "trackPlans"
            ? "View Actions"
            : viewFilter === "labs"
              ? "View Lab Results"
              : "Compare Results"}
      </h2>

      {viewFilter === "assessments" ? (
        assessments.length > 0 ? (
          <>
            <select
              value={selectedId}
              onChange={handleChange}
              style={{
                padding: "12px",
                marginBottom: "10px",
                width: "100%",
                textAlign: "center",
                textAlignLast: "center",
                fontSize: 16,
                borderRadius: 8,
                border: "2px solid #0067B3",
                boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
              }}
            >
              {assessments.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.CompletedDate} at {a.CompletedTime}
                </option>
              ))}
            </select>

            <button
              onClick={handleDownloadPdf}
              style={{
                backgroundColor: "#0067B3",
                color: "white",
                padding: "12px",
                marginBottom: "10px",
                width: "100%",
                fontSize: 16,
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
              }}
            >
              📤 Download PDF
            </button>
            <button
              onClick={() => openConfirmReview(selectedId, "assessment")}
              disabled={!selectedId}
              style={{
                backgroundColor: selectedId ? "#2F5D50" : "#ccc",
                color: "white",
                padding: "12px",
                marginBottom: "10px",
                borderRadius: 8,
                cursor: selectedId ? "pointer" : "not-allowed",
                width: "100%",
              }}
            >
              Finalize Assessment Review
            </button>

            {/* Replace the whole big <div> with this: */}
            <div
              style={{
                maxHeight: 650,
                overflowY: "auto",
                backgroundColor: "#f0f4fb",
                padding: "12px",
                marginBottom: "30px",
                width: "100%",
                fontSize: 16,
                borderRadius: 8,
                border: "2px solid #0067B3",
                boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
              }}
            >
              <div id="radialChartContainer">
                <WholeHealthReportDonut scores={assessmentScores} />
              </div>
              <WholeHealthReportLegend />

              <AssessmentDetails
                answers={selectedAssessment} // <-- pass original answers here
                assessmentScores={assessmentScores}
                categoryThresholds={{
                  CMScore: [
                    { min: 0, max: 1, color: "#28a745" },
                    { min: 2, max: 3, color: "#ffc107" },
                    { min: 4, max: 48, color: "#B00000" },
                  ],

                  MHScore: [
                    { min: 0, max: 16, color: "#28a745" },
                    { min: 17, max: 32, color: "#ffc107" },
                    { min: 33, max: 66, color: "#B00000" },
                  ],
                  PHScore: [
                    { min: 0, max: 5, color: "#28a745" },
                    { min: 6, max: 10, color: "#ffc107" },
                    { min: 11, max: 30, color: "#B00000" },
                  ],
                  SHScore: [
                    { min: 0, max: 7, color: "#28a745" },
                    { min: 8, max: 14, color: "#ffc107" },
                    { min: 15, max: 28, color: "#B00000" },
                  ],
                  SoHScore: [
                    { min: 0, max: 12, color: "#28a745" },
                    { min: 13, max: 24, color: "#ffc107" },
                    { min: 25, max: 48, color: "#B00000" },
                  ],
                }}
              />
              {professionalReviews.length > 0 ? (
                <section
                  style={{
                    marginTop: 40,
                    padding: 16,
                    borderTop: "2px solid #0067B3",
                    width: isMobile ? "100%" : "100%",
                    backgroundColor: "#e6f0ff",
                    borderRadius: 8,
                  }}
                >
                  <h3 style={{ color: "#0067B3", marginBottom: 16 }}>
                    Professional Reviews
                  </h3>
                  <ul style={{ listStyleType: "none", padding: 0 }}>
                    {professionalReviews.map((review) => (
                      <li
                        key={review.id}
                        style={{
                          backgroundColor: "#fff",
                          padding: 12,
                          width: "100%",
                          marginBottom: 12,
                          borderRadius: 6,
                          boxShadow: "0 1px 3px rgba(0, 74, 153, 0.2)",
                        }}
                      >
                        <strong>Name:</strong> {review.ProfessionalName} <br />
                        <strong>Role:</strong> {review.ProfessionalRole} <br />
                        <strong>Date:</strong> {review.ReviewDate}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : (
                <p style={{ marginTop: 40, color: "#333" }}>
                  No professional reviews for this assessment.
                </p>
              )}
            </div>
          </>
        ) : (
          <p style={{ textAlign: "center", color: "#333", fontSize: 16 }}>
            No Assessment Found.
          </p>
        )
      ) : viewFilter === "trackPlans" ? (
        <div
          style={{
            textAlign: "center",
            width: "100%",
            marginTop: 20,
            alignItems: "center",
            flexDirection: "column",
            display: "flex",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              marginBottom: 12,
              marginTop: -30,
              flexWrap: "wrap", // allows wrapping on small screens
              width: "100%",
              marginLeft: "auto",
              marginRight: "auto",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ width: "100%" }}>
              <label
                htmlFor="start-date"
                style={{
                  display: "block",
                  marginBottom: 1,
                  fontWeight: "600",
                  color: "#0067B3",
                }}
              >
                Start Date
              </label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1.5px solid #0067B3",
                  fontSize: 16,
                  boxShadow: "0 1px 3px rgba(0, 74, 153, 0.2)",
                }}
              />
            </div>

            <div style={{ width: "100%" }}>
              <label
                htmlFor="end-date"
                style={{
                  display: "block",
                  marginBottom: 1,
                  fontWeight: "600",
                  color: "#0067B3",
                }}
              >
                End Date
              </label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                min={startDate}
                max={todayStr}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1.5px solid #0067B3",
                  fontSize: 16,
                  boxShadow: "0 1px 3px rgba(0, 74, 153, 0.2)",
                }}
              />
            </div>
          </div>

          {filteredPlans.length > 0 ? (
            <>
              {/* 📤 Download Button */}
              <button
                onClick={() => downloadTrackPlansPDF(showPercentages)}
                style={{
                  backgroundColor: "#0067B3",
                  color: "white",
                  padding: "0 20px",
                  borderRadius: 8,
                  fontSize: 14,
                  cursor: "pointer",
                  width: "100%",
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: 0,
                  marginBottom: 12,
                }}
              >
                📤 Download Plans PDF
              </button>
              <button
                onClick={() => {
                  if (filteredPlans.length > 0)
                    openConfirmReview(filteredPlans[0].id, "health plan");
                  else alert("No health plan found.");
                }}
                style={{
                  backgroundColor:
                    filteredPlans.length > 0 ? "#2F5D50" : "#ccc",
                  color: "white",
                  padding: "0 20px",
                  borderRadius: 8,
                  height: 44,
                  fontSize: 14,
                  cursor: filteredPlans.length > 0 ? "pointer" : "not-allowed",
                  width: "100%",
                  marginBottom: 12,
                }}
              >
                Finalize Plan Review
              </button>
              <button
                onClick={() => setShowPercentages((prev) => !prev)}
                style={{
                  backgroundColor: showPercentages ? "#8C1456" : "#2F5D50",
                  color: "white",
                  padding: "0 20px",
                  borderRadius: 8,
                  fontSize: 14,
                  width: "100%",
                  height: 44,
                  cursor: "pointer",
                  marginTop: 0,
                  marginBottom: 12,
                }}
              >
                {showPercentages ? "Hide" : "Show"} Progress Percentages
              </button>

              {/* 📄 Content to Capture in PDF */}
              <div ref={trackPlanRef} style={{ width: "100%" }}>
                <ul
                  style={{
                    maxHeight: 650,
                    overflowY: "auto",
                    backgroundColor: "#f0f4fb",
                    padding: "12px",
                    marginBottom: "30px",
                    width: "100%",
                    fontSize: 16,
                    borderRadius: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 16,
                    border: "2px solid #0067B3",
                    boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
                    marginLeft: "auto",
                    marginRight: "auto", // ✅ centers the block
                    boxSizing: "border-box",
                  }}
                >
                  <h3 style={{ color: "#0067B3", marginBottom: 16 }}>
                    Selected Actions
                  </h3>
                  {filteredPlans.map((plan) => (
                    <div
                      key={plan.id}
                      style={{
                        ...styles.planCard,
                        width: "100%",
                        background: "#ffffff",
                        padding: "20px",
                        borderRadius: 8,
                        marginBottom: 20,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <h2 style={styles.planTitle}>{plan.Action}</h2>
                      <p style={styles.planText}>
                        <strong>Question:</strong> {plan.ActionQuestion}
                      </p>
                      <p style={styles.planText}>
                        <strong>Start Date:</strong>{" "}
                        {plan.CompletedDate || "Unknown"}
                      </p>
                      <p style={styles.planText}>
                        <strong>Frequency:</strong> {plan.Frequency}
                      </p>
                      <p style={styles.planText}>
                        <strong>Note:</strong> {plan.ActionNote}
                      </p>

                      {plan.Discontinued === true && plan.DiscontinuedDate && (
                        <p
                          style={{
                            color: "#8C1456",
                            fontWeight: "600",
                            marginTop: 4,
                          }}
                        >
                          Discontinued: {plan.DiscontinuedDate}
                        </p>
                      )}
                      {plan.Successful === true && plan.SuccessfulDate && (
                        <p
                          style={{
                            color: "#2F5D50",
                            fontWeight: "600",
                            marginTop: 4,
                          }}
                        >
                          Successful: {plan.SuccessfulDate}
                        </p>
                      )}

                      {showPercentages && (
                        <div
                          style={{
                            marginTop: 20,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <PercentageWheel percentage={plan.Percentage || 0} />
                        </div>
                      )}
                    </div>
                  ))}
                  {healthPlanReviews.length > 0 ? (
                    <section
                      style={{
                        marginTop: 40,
                        padding: 16,
                        borderTop: "2px solid #0067B3",
                        width: isMobile ? "100%" : "100%",
                        backgroundColor: "#e6f0ff",
                        borderRadius: 8,
                      }}
                    >
                      <h3 style={{ color: "#0067B3", marginBottom: 16 }}>
                        Professional Reviews
                      </h3>
                      <ul style={{ listStyleType: "none", padding: 0 }}>
                        {healthPlanReviews.map((review) => (
                          <li
                            key={review.id}
                            style={{
                              backgroundColor: "#fff",
                              padding: 12,
                              width: "100%",
                              marginBottom: 12,
                              borderRadius: 6,
                              boxShadow: "0 1px 3px rgba(0, 74, 153, 0.2)",
                            }}
                          >
                            <strong>Name:</strong> {review.ProfessionalName}{" "}
                            <br />
                            <strong>Role:</strong> {review.ProfessionalRole}{" "}
                            <br />
                            <strong>Date:</strong> {review.ReviewDate}
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : (
                    <p style={{ marginTop: 40, color: "#333" }}>
                      No professional reviews for this health plan.
                    </p>
                  )}
                </ul>
              </div>
            </>
          ) : (
            <p style={{ color: "#333" }}>No Health Plan Found.</p>
          )}
        </div>
      ) : viewFilter === "compare" ? (
        <div
          style={{
            width: isMobile ? "100%" : 350,
            maxWidth: "100%",
            margin: "0 auto",
          }}
        >
          {/* 🔽 NEW button here */}
          <button
            type="button"
            onClick={handleDownloadComparePdf}
            style={{
              backgroundColor: "#0067B3",
              color: "white",
              padding: "12px 24px",
              borderRadius: 8,
              fontSize: 14,
              cursor: "pointer",
              width: "100%",
              margin: "0 auto 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            📤 Download Compare PDF
          </button>
          {/* Toggle what we're comparing */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <button
              type="button"
              onClick={() => setCompareMode("assessments")}
              style={{
                padding: "8px 18px",
                borderRadius: 10,
                border:
                  compareMode === "assessments"
                    ? "2px solid #0067B3"
                    : "2px solid #ccc",
                backgroundColor:
                  compareMode === "assessments" ? "#d9eaff" : "#fff",
                color: compareMode === "assessments" ? "#0067B3" : "#333",
                fontWeight: 600,
                cursor: "pointer",
                minWidth: isMobile ? "100%" : 110,
                marginBottom: isMobile ? 8 : 0,
              }}
            >
              Compare Assessments
            </button>
            <button
              type="button"
              onClick={() => setCompareMode("labs")}
              style={{
                padding: "8px 18px",
                borderRadius: 10,
                border:
                  compareMode === "labs"
                    ? "2px solid #0067B3"
                    : "2px solid #ccc",
                backgroundColor: compareMode === "labs" ? "#d9eaff" : "#fff",
                color: compareMode === "labs" ? "#0067B3" : "#333",
                fontWeight: 600,
                cursor: "pointer",
                minWidth: isMobile ? "100%" : 110,
                marginBottom: isMobile ? 8 : 0,
              }}
            >
              Compare Lab Results
            </button>
          </div>

          {compareMode === "assessments" ? (
            // ===== Assessments Compare =====
            assessments.length < 2 ? (
              <p style={{ textAlign: "center", color: "#333" }}>
                You need at least two completed assessments to compare.
              </p>
            ) : (
              <>
                {/* Pick left/right assessments */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: 16,
                    marginBottom: 24,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 4,
                        color: "#0067B3",
                      }}
                    >
                      Pre/Oldest
                      <br />
                      Assessment
                    </label>
                    <select
                      value={compareLeftAssessmentId || ""}
                      onChange={(e) =>
                        setCompareLeftAssessmentId(e.target.value || null)
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        borderRadius: 8,
                        border: "2px solid #0067B3",
                      }}
                    >
                      <option value="">Select assessment</option>
                      {assessments.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.CompletedDate} at {a.CompletedTime}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 4,
                        color: "#0067B3",
                      }}
                    >
                      Post/Newest
                      <br />
                      Assessment
                    </label>
                    <select
                      value={compareRightAssessmentId || ""}
                      onChange={(e) =>
                        setCompareRightAssessmentId(e.target.value || null)
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        borderRadius: 8,
                        border: "2px solid #0067B3",
                      }}
                    >
                      <option value="">Select Assessment</option>
                      {assessments.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.CompletedDate} at {a.CompletedTime}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Side-by-side domain scores table */}
                {compareLeftAssessmentId && compareRightAssessmentId ? (
                  (() => {
                    const leftAssessment = assessments.find(
                      (a) => a.id === compareLeftAssessmentId,
                    );
                    const rightAssessment = assessments.find(
                      (a) => a.id === compareRightAssessmentId,
                    );

                    if (!leftAssessment || !rightAssessment) {
                      return (
                        <p style={{ textAlign: "center", color: "#333" }}>
                          Could not load selected assessments.
                        </p>
                      );
                    }

                    const leftHeader = leftAssessment.CompletedDate
                      ? `${leftAssessment.CompletedDate}${
                          leftAssessment.CompletedTime
                            ? ` at ${leftAssessment.CompletedTime}`
                            : ""
                        }`
                      : "Pre/Oldest Assessment";

                    const rightHeader = rightAssessment.CompletedDate
                      ? `${rightAssessment.CompletedDate}${
                          rightAssessment.CompletedTime
                            ? ` at ${rightAssessment.CompletedTime}`
                            : ""
                        }`
                      : "Post/Newest Assessment";

                    const formatDiff = (leftRaw, rightRaw) => {
                      const leftNum = parseFloat(leftRaw);
                      const rightNum = parseFloat(rightRaw);
                      if (Number.isNaN(leftNum) || Number.isNaN(rightNum))
                        return "—";

                      const diff = +(rightNum - leftNum).toFixed(2); // B - A

                      if (diff > 0) return `+${diff}`;
                      if (diff < 0) return `${diff}`;
                      return "0";
                    };

                    const getTrendColor = (leftRaw, rightRaw) => {
                      const leftNum = parseFloat(leftRaw);
                      const rightNum = parseFloat(rightRaw);

                      if (Number.isNaN(leftNum) || Number.isNaN(rightNum))
                        return "#999999"; // gray

                      const diff = +(rightNum - leftNum).toFixed(2); // B - A

                      // 🔴 Increase > 2 = worse
                      if (diff > 1.99) return "#B00000"; // red
                      // 🟢 Decrease < -2 = better
                      if (diff < -1.99) return "#28a745"; // green

                      return "#C0C0C0"; // neutral (-2 to 2 range)
                    };

                    const domains = [
                      { key: "CMScore", label: "Coping Mechanisms" },
                      { key: "MHScore", label: "Mental Health" },
                      { key: "PHScore", label: "Physical Health" },
                      { key: "SHScore", label: "Spiritual Health" },
                      { key: "SoHScore", label: "Social Health" },
                      { key: "TRScore", label: "Trauma" },
                    ];

                    return (
                      <div
                        style={{
                          overflowX: "auto",
                          backgroundColor: "#f0f4fb",
                          padding: 16,
                          borderRadius: 10,
                          border: "2px solid #0067B3",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: 14,
                          }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: "#d9eaff" }}>
                              <th
                                style={{
                                  border: "1px solid #ccc",
                                  padding: 8,
                                  textAlign: "left", // ✅ first column left-aligned
                                }}
                              >
                                Domain
                              </th>
                              <th
                                style={{
                                  border: "1px solid #ccc",
                                  padding: 8,
                                  textAlign: "center",
                                }}
                              >
                                {leftHeader}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #ccc",
                                  padding: 8,
                                  textAlign: "center",
                                }}
                              >
                                {rightHeader}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #ccc",
                                  padding: 8,
                                  textAlign: "center",
                                }}
                              >
                                Change
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {domains.map(({ key, label }) => {
                              const leftVal = compareLeftScores?.[key];
                              const rightVal = compareRightScores?.[key];

                              const leftDisplay =
                                leftVal === null ||
                                leftVal === undefined ||
                                leftVal === ""
                                  ? "—"
                                  : leftVal.toString();
                              const rightDisplay =
                                rightVal === null ||
                                rightVal === undefined ||
                                rightVal === ""
                                  ? "—"
                                  : rightVal.toString();

                              const diffDisplay = formatDiff(leftVal, rightVal);
                              const trendColor = getTrendColor(
                                leftVal,
                                rightVal,
                              );

                              return (
                                <tr key={key}>
                                  <td
                                    style={{
                                      border: "1px solid #ccc",
                                      padding: 8,
                                      fontWeight: 600,
                                      color: "#0067B3",
                                      textAlign: "left", // ✅ first column left-aligned
                                    }}
                                  >
                                    {label}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #ccc",
                                      padding: 8,
                                      textAlign: "center",
                                    }}
                                  >
                                    {leftDisplay}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #ccc",
                                      padding: 8,
                                      textAlign: "center",
                                    }}
                                  >
                                    {rightDisplay}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #ccc",
                                      padding: 8,
                                      textAlign: "left",
                                      fontWeight: 600,
                                      color: trendColor,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                        gap: 6,
                                        paddingLeft: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: "50%",
                                          backgroundColor: trendColor,
                                          display: "inline-block",
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span>{diffDisplay}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <p style={{ textAlign: "center", color: "#333" }}>
                    Select both assessments above to see the comparison.
                  </p>
                )}
              </>
            )
          ) : // ===== Labs Compare =====
          (labResults?.length || 0) < 2 ? (
            <p style={{ textAlign: "center", color: "#333" }}>
              You need at least two lab entries to compare.
            </p>
          ) : (
            <>
              {/* Select labs A/B */}
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 16,
                  marginBottom: 24,
                  maxWidth: isMobile ? "100%" : 600,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 4,
                      color: "#0067B3",
                      fontWeight: 600,
                    }}
                  >
                    Pre/Oldest
                    <br />
                    Lab
                  </label>
                  <select
                    value={compareLeftLabId || ""}
                    onChange={(e) =>
                      setCompareLeftLabId(e.target.value || null)
                    }
                    style={{
                      width: "100%",
                      padding: 12,
                      borderRadius: 8,
                      border: "2px solid #0067B3",
                      fontSize: 16,
                      boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
                    }}
                  >
                    <option value="">Select lab result</option>
                    {labResults.map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.CompletedDate || "Unknown date"}
                        {lab.CompletedTime ? ` at ${lab.CompletedTime}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 4,
                      color: "#0067B3",
                      fontWeight: 600,
                    }}
                  >
                    Post/Newest
                    <br />
                    Lab
                  </label>
                  <select
                    value={compareRightLabId || ""}
                    onChange={(e) =>
                      setCompareRightLabId(e.target.value || null)
                    }
                    style={{
                      width: "100%",
                      padding: 12,
                      borderRadius: 8,
                      border: "2px solid #0067B3",
                      fontSize: 16,
                      boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
                    }}
                  >
                    <option value="">Select lab result</option>
                    {labResults.map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.CompletedDate || "Unknown date"}
                        {lab.CompletedTime ? ` at ${lab.CompletedTime}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Labs side-by-side */}
              {compareLeftLabId && compareRightLabId ? (
                (() => {
                  const leftLab = labResults.find(
                    (l) => l.id === compareLeftLabId,
                  );
                  const rightLab = labResults.find(
                    (l) => l.id === compareRightLabId,
                  );

                  if (!leftLab || !rightLab) {
                    return (
                      <p style={{ textAlign: "center", color: "#333" }}>
                        Could not load selected lab results.
                      </p>
                    );
                  }

                  const leftHeader = leftLab.CompletedDate || "Lab A";
                  const rightHeader = rightLab.CompletedDate || "Lab B";

                  const formatDiff = (leftRaw, rightRaw) => {
                    const leftNum = parseFloat(leftRaw);
                    const rightNum = parseFloat(rightRaw);

                    if (Number.isNaN(leftNum) || Number.isNaN(rightNum)) {
                      return "—";
                    }

                    const diff = +(rightNum - leftNum).toFixed(2); // B - A

                    if (diff > 0) return `+${diff}`;
                    if (diff < 0) return `${diff}`;
                    return "0";
                  };

                  return (
                    <div
                      style={{
                        overflowX: "auto",
                        backgroundColor: "#f0f4fb",
                        padding: 16,
                        borderRadius: 10,
                        border: "2px solid #0067B3",
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#d9eaff" }}>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: 8,
                                textAlign: "left",
                              }}
                            >
                              Results
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: 8,
                                textAlign: "center",
                              }}
                            >
                              {leftHeader}
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: 8,
                                textAlign: "center",
                              }}
                            >
                              {rightHeader}
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: 8,
                                textAlign: "center",
                              }}
                            >
                              Difference
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {labsFields.map(({ key, label }) => {
                            const leftRaw = leftLab[key];
                            const rightRaw = rightLab[key];

                            const leftDisplay =
                              leftRaw === null ||
                              leftRaw === undefined ||
                              leftRaw === ""
                                ? "—"
                                : leftRaw.toString();
                            const rightDisplay =
                              rightRaw === null ||
                              rightRaw === undefined ||
                              rightRaw === ""
                                ? "—"
                                : rightRaw.toString();

                            const diffDisplay = formatDiff(leftRaw, rightRaw);

                            return (
                              <tr key={key}>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: 6,
                                    fontWeight: 600,
                                    color: "#0067B3",
                                    textAlign: "left",
                                  }}
                                >
                                  {label}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: 6,
                                    textAlign: "center",
                                  }}
                                >
                                  {leftDisplay}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: 6,
                                    textAlign: "center",
                                  }}
                                >
                                  {rightDisplay}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: 6,
                                    textAlign: "center",
                                    fontWeight: 600,
                                  }}
                                >
                                  {diffDisplay}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              ) : (
                <p style={{ textAlign: "center", color: "#333" }}>
                  Select both lab entries above to see the comparison.
                </p>
              )}
            </>
          )}
        </div>
      ) : /* NEW: labs JSX */
      labResults.length > 0 ? (
        <>
          <select
            value={selectedLabId || ""}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedLabId(id);
              const found = labResults.find((l) => l.id === id);
              setSelectedLab(found || null);
            }}
            style={{
              padding: "12px",
              marginBottom: "10px",
              width: "100%",
              fontSize: 16,
              borderRadius: 8,
              border: "2px solid #0067B3",
              boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
            }}
          >
            {labResults.map((l) => (
              <option key={l.id} value={l.id}>
                {l.CompletedDate || "Unknown date"}{" "}
                {l.CompletedTime ? `at ${l.CompletedTime}` : ""}
              </option>
            ))}
          </select>

          {/* Download PDF button for labs (you can wire to your labs-PDF function) */}
          <button
            onClick={handleDownloadLabPdf}
            style={{
              backgroundColor: "#0067B3",
              color: "white",
              padding: "12px 24px",
              borderRadius: 8,
              fontSize: 14,
              cursor: "pointer",
              width: "100%",
              margin: "0 auto 10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            📤 Download Lab Results PDF
          </button>

          <div
            style={{
              maxHeight: 650,
              overflowY: "auto",
              backgroundColor: "#f0f4fb",
              padding: "12px",
              marginBottom: "30px",
              width: "100%",
              fontSize: 16,
              borderRadius: 8,
              border: "2px solid #0067B3",
              boxShadow: "0 2px 6px rgba(0, 74, 153, 0.3)",
            }}
          >
            {selectedLab ? (
              (() => {
                // Group fields by category for this lab
                const grouped = {
                  Lipids: [],
                  Minerals: [],
                  Vitamins: [],
                  Other: [],
                };

                labsFields.forEach((field) => {
                  const raw = selectedLab[field.key];
                  if (raw === null || raw === undefined || raw === "") return; // skip blanks

                  const display = raw.toString();
                  const dotColor = getLabDotColor(field.key, raw);
                  const category = getLabCategory(field.key);

                  grouped[category].push({
                    field,
                    display,
                    dotColor,
                  });
                });

                const sectionOrder = [
                  "Lipids",
                  "Minerals",
                  "Vitamins",
                  "Other",
                ];

                return (
                  <div>
                    {sectionOrder.map((section) => {
                      const items = grouped[section];
                      if (!items || items.length === 0) return null;

                      return (
                        <div key={section} style={{ marginBottom: 20 }}>
                          <h3
                            style={{
                              margin: "12px 0 8px",
                              color: "#0067B3",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              paddingBottom: 4,
                            }}
                          >
                            {section}
                          </h3>
                          <ul
                            style={{
                              listStyleType: "none",
                              padding: 0,
                              margin: 0,
                            }}
                          >
                            {items.map(({ field, display, dotColor }) => (
                              <li
                                key={field.key}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 10,
                                  marginBottom: 8,
                                  color: "#333",
                                }}
                              >
                                <span
                                  style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: dotColor,
                                    flexShrink: 0,
                                    marginTop: 4,
                                  }}
                                />
                                <span
                                  style={{
                                    flex: 1,
                                    textAlign: "left",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  <strong>{field.label}</strong>: {display}{" "}
                                  <span style={{ color: "#555", fontSize: 12 }}>
                                    (Reference: {field.rangeText})
                                  </span>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <p style={{ color: "#333" }}>No lab result selected.</p>
            )}
          </div>
        </>
      ) : (
        <p style={{ textAlign: "center", color: "#333", fontSize: 16 }}>
          No Lab Results Found.
        </p>
      )}
      {confirmReview &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 1000,
              display: "block",
            }}
            onClick={() => setConfirmReview(false)}
          >
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: popupCenterX,
                transform: "translate(-50%, -50%)",
                padding: 16,
                maxWidth: "calc(100vw - 32px)",
                boxSizing: "border-box",
                zIndex: 1001,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  backgroundColor: "white",
                  padding: 24,
                  borderRadius: 12,
                  maxWidth: 400,
                  width: "90vw",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  textAlign: "center",
                }}
              >
                <h2>Confirm Review</h2>
                <p>
                  Are you sure you want to finalize the review for this{" "}
                  {pendingReview.type === "assessment"
                    ? "assessment"
                    : "health plan"}
                  ?
                </p>
                <div
                  style={{
                    marginTop: 24,
                    display: "flex",
                    justifyContent: "center",
                    gap: 16,
                    width: "100%",
                  }}
                >
                  <button
                    onClick={handleConfirmReview}
                    style={{
                      padding: "10px 20px",
                      width: 120,
                      borderRadius: 8,
                      backgroundColor: "#0067B3",
                      color: "white",
                      cursor: "pointer",
                    }}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setConfirmReview(false)}
                    style={{
                      padding: "10px 20px",
                      width: 120,
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
